import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { FileText } from "lucide-react";
import {
  FaUpload,
  FaPlus,
  FaTimes,
  FaEye,
  FaDownload,
  FaTrash,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
  FaCheckCircle,
  FaQuestionCircle,
  FaFolderOpen,
  FaShareAlt,
  FaLock,
  FaFilePdf,
  FaImage,
} from "react-icons/fa";
import { toast } from "react-toastify";
import subjectsData from "../data/subjectsData.json";
import staticEvaluatedSheets from "../data/staticEvaluatedSheets.js";
import { generatePdfThumbnail } from "../utils/pdfThumbnail";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";
import { UserContext } from "../contexts/UserContext";
import { MathJaxContext } from "better-react-mathjax";
import MathJaxRenderer from "./MathJaxRenderer";
import { useWorksheet } from "../sse/WorksheetProvider.jsx";
import { useClearChat } from "../contexts/ClearChatContext";
import DoubtSolver from "./DoubtSolver";
import RateLimitModal from "./RateLimitModal";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";
const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
// Basic markdown to HTML for display (limited to bold **...**)
const applyBasicMarkdown = (text) => {
  if (!text) return text;
  try {
    return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  } catch {
    return text;
  }
};

const pickFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return null;
};

const normalizeUrlList = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (typeof item === "string") {
        const trimmed = item.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (item && typeof item === "object") {
        const candidate =
          item.url ||
          item.src ||
          item.imageUrl ||
          item.image_url ||
          item.path ||
          item.fileUrl ||
          item.file_url;
        if (typeof candidate === "string") {
          const normalized = candidate.trim();
          if (normalized.length > 0) {
            return normalized;
          }
        }
      }
      return null;
    })
    .filter(Boolean);
};

const decodeAnswerImagesPayload = (items = []) => {
  const decoded = {
    pdfDataUris: [],
    imageEntries: [],
  };

  if (!Array.isArray(items)) {
    return decoded;
  }

  items.forEach((item = {}, index) => {
    const contentType =
      typeof item?.content_type === "string"
        ? item.content_type.trim()
        : typeof item?.mimeType === "string"
        ? item.mimeType.trim()
        : typeof item?.type === "string"
        ? item.type.trim()
        : "";
    const rawData =
      typeof item?.data === "string" && item.data.trim().length > 0
        ? item.data.trim()
        : "";
    if (!rawData) {
      return;
    }

    const dataUri = rawData.startsWith("data:")
      ? rawData
      : `data:${contentType || "application/octet-stream"};base64,${rawData}`;
    const label =
      (typeof item?.filename === "string" && item.filename.trim().length > 0
        ? item.filename.trim()
        : typeof item?.name === "string" && item.name.trim().length > 0
        ? item.name.trim()
        : `Answer file ${index + 1}`) || `Answer file ${index + 1}`;

    const fileType =
      typeof item?.file_type === "string"
        ? item.file_type.toLowerCase()
        : typeof item?.type === "string"
        ? item.type.toLowerCase()
        : "";

    if (
      (contentType && contentType.toLowerCase().includes("pdf")) ||
      fileType === "pdf"
    ) {
      decoded.pdfDataUris.push(dataUri);
    } else {
      decoded.imageEntries.push({ src: dataUri, label });
    }
  });

  return decoded;
};

const normalizeWorksheetAssets = (payload = {}) => {
  const data = payload?.data ?? payload ?? {};
  const pdfUrl = pickFirstNonEmptyString(
    data.pdfUrl,
    data.pdf_url,
    data.pdf,
    data.pdfLink,
    data.pdf_link,
    data.answersPdfUrl,
    data.answerPdfUrl,
    data.fileUrl,
    data.file_url
  );

  const answerImagesPayload = Array.isArray(data.answerImages)
    ? data.answerImages
    : Array.isArray(payload?.answerImages)
    ? payload.answerImages
    : [];
  const decodedAnswerImages = decodeAnswerImagesPayload(answerImagesPayload);

  const possibleCollections = [
    data.images,
    data.imageUrls,
    data.image_urls,
    data.answerImages,
    data.answer_images,
    data.pages,
    data.files,
    data.fileUrls,
    data.file_urls,
  ];

  let imageUrls = decodedAnswerImages.imageEntries.slice();
  for (const collection of possibleCollections) {
    const urls = normalizeUrlList(collection);
    if (urls.length > 0) {
      const wrapped = urls.map((url, index) => ({
        src: url,
        label: `Worksheet page ${index + 1}`,
      }));
      if (wrapped.length > 0) {
        imageUrls =
          imageUrls.length > 0 ? imageUrls.concat(wrapped) : wrapped.slice();
        break;
      }
      break;
    }
  }

  const chosenPdfUrl = decodedAnswerImages.pdfDataUris[0] || pdfUrl || null;

  return { pdfUrl: chosenPdfUrl, imageUrls };
};

const EVALUATE_UPLOAD_TABS = [
  {
    id: "worksheet",
    label: "Aanya worksheet",
    icon: FaFolderOpen,
  },
  {
    id: "upload",
    label: "Offline worksheet",
    icon: FaUpload,
  },
];

const FeedbackContent = ({ row, onAskDoubt }) => {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [preview, setPreview] = useState("");
  const containerRef = useRef(null); // the visible area wrapper
  const contentRef = useRef(null); // the full content (for measuring lines)

  const feedback = row?.feedback || "-";
  const marks = row?.marks ?? 0;
  const maxMarks = row?.maxMarks ?? 0;

  // ----- Utilities -----
  const getLineHeight = (el) => {
    const cs = window.getComputedStyle(el);
    let lh = parseFloat(cs.lineHeight);
    if (!lh || Number.isNaN(lh)) {
      const fs = parseFloat(cs.fontSize) || 16;
      lh = fs * 1.2;
    }
    return lh;
  };

  // Count visual lines robustly even with MathJax by clustering rects vertically
  const countVisualLines = (el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    const rects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0
    );
    if (!rects.length) return 0;

    const lh = getLineHeight(el);
    const THRESH = Math.max(2, lh * 0.4);
    const MIN_HEIGHT = Math.max(1, lh * 0.35);

    rects.sort((a, b) => a.top - b.top);

    let lines = 0,
      currentTop = null;
    for (const r of rects) {
      if (r.height < MIN_HEIGHT) continue; // ignore tiny MathJax fragments
      if (currentTop === null || Math.abs(r.top - currentTop) > THRESH) {
        lines += 1;
        currentTop = r.top;
      }
    }
    return lines;
  };

  // Estimate ~chars per line using canvas (width & font-aware)
  const estimateCharsPerLine = (el) => {
    const cs = window.getComputedStyle(el);
    const font = `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${cs.fontSize} / ${cs.lineHeight} ${cs.fontFamily}`;
    const width = el.clientWidth || 0;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = font;

    const sample =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ 0123456789";
    const w = ctx.measureText(sample).width || 1;
    const avgChar = w / sample.length; // average char width in current font
    if (avgChar <= 0) return 50; // fallback
    return Math.max(10, Math.floor(width / avgChar));
  };

  const makePreview = (sourceEl, text) => {
    if (!sourceEl) return "";
    // strip newlines to avoid super short lines in preview
    const clean = String(text).replace(/\s+/g, " ").trim();
    const cpl = estimateCharsPerLine(sourceEl);

    // Keep a little safety margin; words only
    const maxChars = Math.max(20, Math.floor(cpl * 2 * 0.95));

    if (clean.length <= maxChars) return clean;

    // Cut at the last word boundary before maxChars
    let cut = clean.slice(0, maxChars);
    const lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > 0) cut = cut.slice(0, lastSpace);

    return cut.trim() + "…";
  };

  // ----- Measure & decide toggle -----
  const measure = () => {
    const content = contentRef.current;
    const container = containerRef.current;
    if (!content || !container) return;

    // Temporarily ensure un-clamped for measuring actual lines
    const prevDisplay = content.style.display;
    content.style.display = "block";

    // Wait a frame so MathJax layout is settled
    requestAnimationFrame(() => {
      const lines = countVisualLines(content);
      setShowToggle(lines > 2);

      // Build preview now (so it matches current width/font)
      setPreview(makePreview(container, content.textContent || ""));

      // restore
      content.style.display = prevDisplay || "";
    });
  };

  useEffect(() => {
    measure();

    // Responsive & MathJax changes
    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);

    const mo = new MutationObserver(() => measure());
    if (contentRef.current)
      mo.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });

    const onWinResize = () => measure();
    window.addEventListener("resize", onWinResize);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", onWinResize);
    };
  }, [feedback]);

  // ----- Render -----
  return (
    <>
      {feedback && feedback !== "-" ? (
        <>
          {/* Visible wrapper: ensures proper width in table cells */}
          <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
            {/* COLLAPSED: show plain preview (safe truncation) */}

            {/* Always show expanded view. show full MathJax content */}
            <div ref={contentRef} className="text-gray-700">
              <MathJaxRenderer>{applyBasicMarkdown(feedback)}</MathJaxRenderer>
            </div>
          </div>

          {/* {showToggle && (
            <button
              onClick={() => setExpanded((p) => !p)}
              className="text-sm text-blue-500 hover:underline"
            >
              {expanded ? "Read less" : "Read more"}
            </button>
          )} */}
        </>
      ) : (
        <span>-</span>
      )}

      {marks < maxMarks && (
        <div>
          <button
            className="inline-flex items-center gap-1 text-sm font-medium
    text-indigo-600 hover:text-indigo-700 underline-offset-2 hover:underline
    focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 rounded-sm
  "
            onClick={onAskDoubt}
          >
            <FaQuestionCircle className="h-4 w-4" aria-hidden />
            Why did I lose marks?
          </button>
        </div>
      )}
    </>
  );
};

const normalizeStepBreakdown = (rawSteps = []) => {
  if (!Array.isArray(rawSteps)) {
    return [];
  }

  return rawSteps.map((step = {}, index) => ({
    ...step,
    step: step?.step ?? index + 1,
    feedback: step?.feedback ?? "",
    marks: typeof step?.marks === "number" ? step.marks : 0,
    maxMarks: typeof step?.maxMarks === "number" ? step.maxMarks : 0,
  }));
};

const normalizeQuestion = (question = {}, index) => {
  const stepBreakdown = normalizeStepBreakdown(question?.stepBreakdown);
  const answerImages = Array.isArray(question?.answerImages)
    ? question.answerImages
    : [];

  const inferredTotalMarks = stepBreakdown.reduce(
    (acc, step) =>
      acc + (typeof step.maxMarks === "number" ? step.maxMarks : 0),
    0
  );

  const totalMarksValue =
    typeof question?.totalMarks === "number"
      ? question.totalMarks
      : typeof question?.maxMarks === "number"
      ? question.maxMarks
      : inferredTotalMarks;

  return {
    ...question,
    id: question?.id ?? index + 1,
    questionImage: question?.questionImage ?? null,
    answerImages,
    marks: typeof question?.marks === "number" ? question.marks : 0,
    totalMarks: totalMarksValue || 0,
    feedback: question?.feedback ?? "",
    stepBreakdown,
    question_text: question?.question_text ?? "",
    answer_text: question?.answer_text ?? "",
  };
};

const normalizeSheet = (sheet = {}, index) => {
  const questions = Array.isArray(sheet?.questions)
    ? sheet.questions.map((question, questionIndex) =>
        normalizeQuestion(question, questionIndex)
      )
    : [];

  const totalMarks =
    typeof sheet?.totalMarks === "number"
      ? sheet.totalMarks
      : questions.reduce(
          (acc, question) =>
            acc +
            (typeof question.totalMarks === "number" ? question.totalMarks : 0),
          0
        );

  const overallMarks =
    typeof sheet?.overallMarks === "number"
      ? sheet.overallMarks
      : questions.reduce(
          (acc, question) =>
            acc + (typeof question.marks === "number" ? question.marks : 0),
          0
        );

  return {
    ...sheet,
    id: sheet?.id ?? index + 1,
    fileName: sheet?.fileName ?? `Worksheet-${index + 1}`,
    subject: sheet?.subject ?? "General",
    date: sheet?.date ?? "",
    totalMarks,
    overallMarks,
    questions,
  };
};

const mathJaxConfig = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    processEscapes: true,
    processEnvironments: true,
    packages: ["base", "ams", "mhchem"],
  },
  options: {
    enableMenu: false,
  },
};

// Custom loader for EvaluateAnswerSheet with bigger size and animated messages
const EvaluateAnswerSheetLoader = ({
  message = "Generating evaluation results...",
}) => {
  const LOADING_MESSAGES = [
    "Please wait, preparing response…",
    "Thinking…",
    "One moment, almost ready…",
    "Analyzing your question…",
    "Processing evaluation data…",
    "Generating feedback…",
    "Calculating scores…",
    "Preparing results…",
  ];

  const getRandomLoadingMessage = () =>
    LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];

  const [currentMessage, setCurrentMessage] = useState(
    getRandomLoadingMessage()
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage(getRandomLoadingMessage());
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Add CSS animation for bouncing dots
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pulse-grow {
        0% {
          transform: scale(0);
          opacity: 0;
        }
        50% {
          transform: scale(1);
          opacity: 1;
        }
        100% {
          transform: scale(0);
          opacity: 0;
        }
      }
      .animate-pulse-grow {
        animation: pulse-grow 1.2s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[200px]">
      <div className="flex flex-col items-center">
        {/* Bigger dots */}
        <div className="flex justify-center space-x-3 mb-4">
          <div
            className="bg-indigo-500 rounded-full animate-pulse-grow"
            style={{
              animationDelay: "0ms",
              animationDuration: "1.2s",
              width: "20px",
              height: "20px",
            }}
          />
          <div
            className="bg-indigo-500 rounded-full animate-pulse-grow"
            style={{
              animationDelay: "400ms",
              animationDuration: "1.2s",
              width: "20px",
              height: "20px",
            }}
          />
          <div
            className="bg-indigo-500 rounded-full animate-pulse-grow"
            style={{
              animationDelay: "800ms",
              animationDuration: "1.2s",
              width: "20px",
              height: "20px",
            }}
          />
        </div>
        {/* Bigger text */}
        <div
          className="text-lg text-gray-700 font-medium text-center"
          aria-live="polite"
        >
          {currentMessage}
        </div>
      </div>
    </div>
  );
};

const EvaluateAnswerSheet = () => {
  // Read role from UserContext and toggle Step 2 based on role
  const { userData } = useContext(UserContext);
  const { triggerClearChat, hasActiveChat } = useClearChat();
  const isSuperAdmin = (userData?.roleType || "").trim() === "SuperAdmin";
  const STEP2_DISABLED = !isSuperAdmin;
  // State for evaluated sheets list
  const [askDoubtText, setAskDoubtText] = useState("");
  const lastDoubtQuestionKeyRef = useRef(null);
  // ======== Split screen + resizer state (only shown after Need Help) ========
  const [leftPanelWidth, setLeftPanelWidth] = useState(60); // %
  const [leftInfoPanelWidth, setLeftInfoPanelWidth] = useState(30); // %
  const [centerPanelWidthNoSide, setCenterPanelWidthNoSide] = useState(70); // %
  const [isLeftResizing, setIsLeftResizing] = useState(false);
  const resizeRef = useRef(null);
  const draggingRef = useRef(false);
  const dragTargetRef = useRef(null);
  const [leftPanelViewer, setLeftPanelViewer] = useState({
    pdfUrl: null,
    imageUrls: [],
  });
  const [isLeftPanelViewerLoading, setIsLeftPanelViewerLoading] =
    useState(false);
  const [leftPanelViewerError, setLeftPanelViewerError] = useState(null);
  const leftPanelViewerAbortControllerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  // NEW: gate the right panel/resizer visibility until user clicks Need Help
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);
  const deriveRateLimitMessageFromError = useCallback((error) => {
    const rawMessage =
      typeof error === "string"
        ? error
        : typeof error?.message === "string"
        ? error.message
        : "";
    if (!rawMessage) {
      return null;
    }
    const normalized = rawMessage.toLowerCase();
    if (
      normalized.includes("rate limit") ||
      normalized.includes("rate_limit") ||
      normalized.includes("rate-limit") ||
      normalized.includes("rate_limit_exceeded") ||
      normalized.includes("429")
    ) {
      return rawMessage;
    }
    return null;
  }, []);

  const handleAskDoubtPrefill = useCallback(
    (questionKey, doubtText) => {
      if (typeof doubtText !== "string" || doubtText.trim().length === 0) {
        return;
      }

      let normalizedKey = "";
      if (typeof questionKey === "string") {
        normalizedKey = questionKey.trim();
      } else if (
        typeof questionKey === "number" &&
        Number.isFinite(questionKey)
      ) {
        normalizedKey = String(questionKey);
      }

      if (!normalizedKey) {
        normalizedKey = `unknown-${Date.now().toString(36)}`;
      }

      if (hasActiveChat && lastDoubtQuestionKeyRef.current !== normalizedKey) {
        triggerClearChat();
      }

      lastDoubtQuestionKeyRef.current = normalizedKey;
      setAskDoubtText(doubtText);
      setShowSidePanel(true);
    },
    [hasActiveChat, triggerClearChat]
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (showSidePanel && isLeftResizing) {
      setIsLeftResizing(false);
    }
  }, [showSidePanel, isLeftResizing]);

  const handleMouseDown = (e) => {
    if (isMobile || !showSidePanel) return;
    dragTargetRef.current = "right";
    draggingRef.current = true;
    e.preventDefault();
  };

  const handleLeftResizerMouseDown = (e) => {
    if (isMobile || showSidePanel) return;
    dragTargetRef.current = "left";
    draggingRef.current = true;
    setIsLeftResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizeRef.current) return;
      if (!draggingRef.current || isMobile) {
        return;
      }
      const target = dragTargetRef.current;
      const rect = resizeRef.current.getBoundingClientRect();
      if (!rect.width) return;
      const percent = ((e.clientX - rect.left) / rect.width) * 100;
      if (target === "right" && showSidePanel) {
        setLeftPanelWidth(clamp(Math.round(percent), 20, 80));
      } else if (target === "left" && !showSidePanel) {
        const nextWidth = clamp(Math.round(percent), 15, 90);
        setLeftInfoPanelWidth(nextWidth);
        setCenterPanelWidthNoSide(clamp(100 - nextWidth, 10, 85));
      }
    };
    const onMouseUp = () => {
      if (!draggingRef.current) {
        return;
      }
      draggingRef.current = false;
      dragTargetRef.current = null;
      setIsLeftResizing(false);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isMobile, showSidePanel]);
  const shouldShowLeftUtilityPanel = !isMobile && !showSidePanel;
  const shouldShowRightUtilityPanel = !isMobile && showSidePanel;
  const centerPanelWidth = (() => {
    if (isMobile) {
      return "100%";
    }
    if (shouldShowRightUtilityPanel) {
      return `${leftPanelWidth}%`;
    }
    if (shouldShowLeftUtilityPanel) {
      return `${centerPanelWidthNoSide}%`;
    }
    return "100%";
  })();

  // ==========================================================================

  // Create a mapping from subject name to icon
  const subjectIconMap = subjectsData.reduce((map, subject) => {
    map[subject.name] = subject.icon;
    return map;
  }, {});
  const SUBJECT_DEFAULT_ICON =
    "https://api.iconify.design/material-symbols/calculate.svg?color=%230D99FF";

  const rawBaseUrl = import.meta.env.VITE_APP_BASE_URL || "";
  const trimmedBaseUrl = rawBaseUrl.replace(/\/+$/, "");
  const worksheetEvaluationFetchEndpoint = trimmedBaseUrl
    ? `${trimmedBaseUrl}/v2/worksheetEvaluation/`
    : "";
  const worksheetEvaluationStatusEndpointBase = trimmedBaseUrl
    ? `${trimmedBaseUrl}/v2/worksheetEvaluation/status/`
    : "";

  // Helper function to get icon for a subject
  const getSubjectIcon = (subjectName) => {
    const iconCandidate = subjectIconMap[subjectName];
    if (typeof iconCandidate === "string" && iconCandidate.trim().length > 0) {
      return iconCandidate;
    }
    return SUBJECT_DEFAULT_ICON;
  };

  // Evaluated sheets fetched from backend
  const [evaluatedSheets, setEvaluatedSheets] = useState([]);
  const [isEvaluatedSheetsLoading, setIsEvaluatedSheetsLoading] =
    useState(false);
  const [evaluatedSheetsError, setEvaluatedSheetsError] = useState(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchEvaluatedSheets = useCallback(
    async ({ signal, clearExisting = true } = {}) => {
      if (!isMountedRef.current) {
        return;
      }

      setIsEvaluatedSheetsLoading(true);
      setEvaluatedSheetsError(null);

      if (clearExisting) {
        setEvaluatedSheets([]);
      }

      if (!worksheetEvaluationFetchEndpoint) {
        if (!signal?.aborted && isMountedRef.current) {
          setEvaluatedSheetsError("API base URL is not configured.");
          setIsEvaluatedSheetsLoading(false);
        }
        return;
      }

      try {
        const response = await fetch(worksheetEvaluationFetchEndpoint, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to load evaluated sheets (${response.status} ${response.statusText})`
          );
        }

        let payload;
        try {
          payload = await response.json();
        } catch (parseError) {
          console.error(
            "Failed to parse evaluated sheets response:",
            parseError
          );
          throw new Error("Unable to parse evaluated sheets response.");
        }

        if (signal?.aborted || !isMountedRef.current) {
          return;
        }

        const results = Array.isArray(payload?.results) ? payload.results : [];

        const normalized = results.map((sheet, index) =>
          normalizeSheet(sheet, index)
        );

        setEvaluatedSheets(normalized);
      } catch (error) {
        if (signal?.aborted) {
          return;
        }

        console.error("Failed to load evaluated sheets:", error);

        if (!isMountedRef.current) {
          return;
        }

        setEvaluatedSheets([]);
        const message = error?.message || "Failed to load evaluated sheets.";
        setEvaluatedSheetsError(message);
        toast.error("Unable to load evaluated worksheets. Please try again.");
      } finally {
        if (signal?.aborted || !isMountedRef.current) {
          return;
        }
        setIsEvaluatedSheetsLoading(false);
      }
    },
    [worksheetEvaluationFetchEndpoint]
  );

  // useEffect(() => {
  //   if (!isSuperAdmin) {
  //     return;
  //   }

  //   const normalized = staticEvaluatedSheets.map((sheet, index) =>
  //     normalizeSheet(sheet, index)
  //   );

  //   setEvaluatedSheets(normalized);
  //   setIsEvaluatedSheetsLoading(false);
  //   setEvaluatedSheetsError(null);
  // }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) {
      return;
    }

    const controller = new AbortController();

    fetchEvaluatedSheets({
      signal: controller.signal,
      clearExisting: true,
    });

    return () => {
      controller.abort();
    };
  }, [isSuperAdmin, fetchEvaluatedSheets]);

  // State for modals and uploads
  const [showUploadModal, setShowUploadModal] = useState(false);
  const previousShowUploadModalRef = useRef(showUploadModal);

  useEffect(() => {
    if (isSuperAdmin) {
      previousShowUploadModalRef.current = showUploadModal;
      return;
    }

    let controller;

    if (previousShowUploadModalRef.current && !showUploadModal) {
      controller = new AbortController();
      fetchEvaluatedSheets({
        signal: controller.signal,
        clearExisting: false,
      });
    }

    previousShowUploadModalRef.current = showUploadModal;

    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [showUploadModal, isSuperAdmin, fetchEvaluatedSheets]);

  // Load available worksheets when component mounts
  useEffect(() => {
    // Mock worksheet data - replace with actual API call
    const mockWorksheets = [
      {
        id: 1,
        title: "Algebra Practice Worksheet",
        subject: "Mathematics",
        topic: "Algebra",
        questionCount: 15,
        difficulty: "Intermediate",
        createdAt: "2024-01-15T10:30:00Z",
      },
      {
        id: 2,
        title: "Physics Mechanics Worksheet",
        subject: "Physics",
        topic: "Mechanics",
        questionCount: 20,
        difficulty: "Advanced",
        createdAt: "2024-01-14T14:20:00Z",
      },
      {
        id: 3,
        title: "Chemistry Organic Reactions",
        subject: "Chemistry",
        topic: "Organic Chemistry",
        questionCount: 12,
        difficulty: "Intermediate",
        createdAt: "2024-01-13T09:15:00Z",
      },
      {
        id: 4,
        title: "Biology Cell Structure",
        subject: "Biology",
        topic: "Cell Biology",
        questionCount: 18,
        difficulty: "Beginner",
        createdAt: "2024-01-12T16:45:00Z",
      },
    ];
    setAvailableWorksheets(mockWorksheets);
  }, []);

  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  // Already marked toggle (default unchecked)
  const [isAlreadyMarked, setIsAlreadyMarked] = useState(false);
  // Multistep state (used when not already marked)
  const [currentStep, setCurrentStep] = useState(1); // 1..3
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [step1QuestionImages, setStep1QuestionImages] = useState([]); // File[]
  const [step1AnswerImages, setStep1AnswerImages] = useState([]); // File[]
  const [step1QuestionThumbnails, setStep1QuestionThumbnails] = useState(
    new Map()
  ); // Map<File, string> for PDF thumbnails
  const [step1AnswerThumbnails, setStep1AnswerThumbnails] = useState(new Map()); // Map<File, string> for Answer PDF thumbnails

  // New Step 1 upload options
  const [step1UploadOption, setStep1UploadOption] = useState("separate"); // 'separate', 'combined', 'worksheet'
  const [step1CombinedImages, setStep1CombinedImages] = useState([]); // File[] for combined question+answer images
  const [step1CombinedThumbnails, setStep1CombinedThumbnails] = useState(
    new Map()
  ); // Map<File, string> for combined PDF thumbnails
  const [offlineUploadPreviewAssets, setOfflineUploadPreviewAssets] =
    useState(null);
  const [isAnswerSheetMarked, setIsAnswerSheetMarked] = useState(false); // Flag for already marked answer sheet
  const [selectedWorksheet, setSelectedWorksheet] = useState(null); // Selected worksheet from list
  const [availableWorksheets, setAvailableWorksheets] = useState([]); // List of available worksheets
  const activeUploadTab =
    step1UploadOption === "worksheet" ? "worksheet" : "upload";

  const handleUploadTabChange = (tabId) => {
    if (tabId === "worksheet") {
      setStep1UploadOption("worksheet");
      return;
    }
    setStep1UploadOption((prev) => (prev === "worksheet" ? "separate" : prev));
  };
  const [isUploading, setIsUploading] = useState(false); // Loading state for upload
  const [showConfirmationModal, setShowConfirmationModal] = useState(false); // Confirmation modal state
  const [step1ApiResponse, setStep1ApiResponse] = useState(null); // Store API response from Step 1
  const [dropZoneHover, setDropZoneHover] = useState({
    question: false,
    answer: false,
    combined: false,
    worksheetAnswer: false,
  });
  const [isStep3Loading, setIsStep3Loading] = useState(false); // Loading state for Step 3
  const [isStep2Loading, setIsStep2Loading] = useState(false); // Loading state for Step 2
  const [verifiedQuestions, setVerifiedQuestions] = useState(new Set());
  const [previewSheet, setPreviewSheet] = useState(null);
  const [step2ExpandedQuestions, setStep2ExpandedQuestions] = useState(
    new Set([1])
  );
  const [step3ExpandedQuestions, setStep3ExpandedQuestions] = useState(
    new Set([1])
  );
  // Single source of truth for Step 2 rendering
  const [step2Questions, setStep2Questions] = useState([]);
  // Separate state for Step 3 preview rendering (seeded, later replaceable by API)
  const [step3Data, setStep3Data] = useState(null);

  // SSE state management
  const [isSSEProcessing, setIsSSEProcessing] = useState(false);
  const [sseProgress, setSseProgress] = useState(0);
  const [sseStatus, setSseStatus] = useState("idle");
  const [sseError, setSseError] = useState(null);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const sseSubscriptionRef = useRef(null);
  const abortControllerRef = useRef(null);
  const latestRequestIdRef = useRef(null);
  useEffect(() => {
    latestRequestIdRef.current = currentRequestId;
  }, [currentRequestId]);

  const {
    submitWithFormData,
    status: fetchWorksheetStatus,
    cancel: cancelWorksheetRequest,
    subscribe: subscribeToWorksheet,
  } = useWorksheet();

  // SSE cleanup on component unmount
  useEffect(() => {
    return () => {
      if (sseSubscriptionRef.current) {
        try {
          sseSubscriptionRef.current();
        } catch {
          // ignore
        }
        sseSubscriptionRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const selectedSheetQuestions = Array.isArray(selectedSheet?.questions)
    ? selectedSheet.questions
    : [];
  const selectedSheetTotalMarks = Number(selectedSheet?.totalMarks ?? 0);
  const selectedSheetOverallMarks = Number(selectedSheet?.overallMarks ?? 0);
  const selectedSheetPercentage =
    selectedSheetTotalMarks > 0
      ? Math.round((selectedSheetOverallMarks / selectedSheetTotalMarks) * 100)
      : 0;
  const selectedSheetRequestId =
    selectedSheet?.requestId ??
    selectedSheet?.request_id ??
    selectedSheet?.worksheetRequestId ??
    selectedSheet?.worksheet_request_id ??
    selectedSheet?.worksheetId ??
    selectedSheet?.worksheet_id ??
    null;

  const handleShareResults = useCallback(async () => {
    const requestId = selectedSheet?.requestId;
    if (!requestId) {
      toast.error("Shareable link is unavailable for this evaluation");
      return;
    }

    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    const safeRequestId = encodeURIComponent(requestId);
    const shareUrl = origin
      ? `${origin}/evaluateanswersheet/${safeRequestId}`
      : `/evaluateanswersheet/${safeRequestId}`;

    const copyWithFallback = async () => {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        await navigator.clipboard.writeText(shareUrl);
        return true;
      }

      if (typeof document === "undefined") {
        return false;
      }

      try {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.top = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);
        return successful;
      } catch (fallbackError) {
        console.error("Fallback copy failed:", fallbackError);
        return false;
      }
    };

    try {
      const copied = await copyWithFallback();
      if (copied) {
        toast.success("Link copied to clipboard");
      } else {
        toast.warn("Unable to copy link automatically");
      }
    } catch (error) {
      console.error("Error copying evaluation link:", error);
      toast.error("Unable to copy link right now");
    }
  }, [selectedSheet?.requestId]);

  const applyResultsModalUrl = useCallback((requestId) => {
    if (
      typeof window === "undefined" ||
      typeof requestId !== "string" ||
      requestId.trim().length === 0
    ) {
      return;
    }
    const safeRequestId = encodeURIComponent(requestId.trim());
    const targetPath = `/evaluateanswersheet/${safeRequestId}`;
    const currentFullPath =
      window.location.pathname + window.location.search + window.location.hash;

    if (previousResultsUrlRef.current === null) {
      previousResultsUrlRef.current = currentFullPath;
    }

    if (window.location.pathname !== targetPath) {
      window.history.replaceState(window.history.state, "", targetPath);
    }
  }, []);

  const resetResultsModalUrl = useCallback(() => {
    if (typeof window === "undefined") {
      previousResultsUrlRef.current = null;
      return;
    }
    if (previousResultsUrlRef.current !== null) {
      window.history.replaceState(
        window.history.state,
        "",
        previousResultsUrlRef.current
      );
      previousResultsUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!showResultsModal) {
      resetResultsModalUrl();
    }
  }, [showResultsModal, resetResultsModalUrl]);

  useEffect(() => {
    return () => {
      resetResultsModalUrl();
    };
  }, [resetResultsModalUrl]);

  useEffect(() => {
    if (!showResultsModal) {
      if (leftPanelViewerAbortControllerRef.current) {
        leftPanelViewerAbortControllerRef.current.abort();
        leftPanelViewerAbortControllerRef.current = null;
      }
      setLeftPanelViewer({ pdfUrl: null, imageUrls: [] });
      setLeftPanelViewerError(null);
      setIsLeftPanelViewerLoading(false);
      return;
    }

    if (!worksheetEvaluationStatusEndpointBase || !selectedSheetRequestId) {
      setLeftPanelViewer({ pdfUrl: null, imageUrls: [] });
      setLeftPanelViewerError(null);
      setIsLeftPanelViewerLoading(false);
      return;
    }

    const controller = new AbortController();
    if (leftPanelViewerAbortControllerRef.current) {
      leftPanelViewerAbortControllerRef.current.abort();
    }
    leftPanelViewerAbortControllerRef.current = controller;

    setIsLeftPanelViewerLoading(true);
    setLeftPanelViewerError(null);

    const fetchWorksheetStatus = async () => {
      try {
        const response = await fetch(
          `${worksheetEvaluationStatusEndpointBase}${encodeURIComponent(
            selectedSheetRequestId
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load worksheet file (${response.status})`);
        }

        const payload = await response.json();
        console.log("Worksheet evaluation status response:", payload);
        if (controller.signal.aborted) {
          return;
        }

        const normalizedAssets = normalizeWorksheetAssets(payload);
        setLeftPanelViewer(normalizedAssets);
        setLeftPanelViewerError(null);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        console.error("Unable to fetch worksheet file:", error);
        setLeftPanelViewer({ pdfUrl: null, imageUrls: [] });
        setLeftPanelViewerError(
          error?.message || "Unable to load worksheet file."
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsLeftPanelViewerLoading(false);
        }
      }
    };

    fetchWorksheetStatus();

    return () => {
      controller.abort();
      if (leftPanelViewerAbortControllerRef.current === controller) {
        leftPanelViewerAbortControllerRef.current = null;
      }
    };
  }, [
    showResultsModal,
    selectedSheetRequestId,
    worksheetEvaluationStatusEndpointBase,
  ]);

  useEffect(() => {
    return () => {
      if (leftPanelViewerAbortControllerRef.current) {
        leftPanelViewerAbortControllerRef.current.abort();
        leftPanelViewerAbortControllerRef.current = null;
      }
    };
  }, []);

  // State for image preview modal
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewImageAlt, setPreviewImageAlt] = useState("");
  // Preview carousel items: { src: string, isPdf: boolean }
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  // State for collapsible questions
  const [expandedQuestions, setExpandedQuestions] = useState(new Set([1])); // First question open by default
  const previousResultsUrlRef = useRef(null);

  // State for upload form
  const [uploadData, setUploadData] = useState({
    questions: [
      {
        id: 1,
        questionImage: null,
        answerImages: [],
        marks: 0,
      },
    ],
  });
  // --- VIEW STATE MIRRORS (initialize with safe defaults) ---
  const showUploadModalRef = useRef(false);
  const currentStepRef = useRef(1);
  const isStep3LoadingRef = useRef(true);
  const step3DataRef = useRef(null);
  const hasMarkedViewedRef = useRef(false); // latch to avoid double-mark

  // --- KEEP REFS IN SYNC WITH STATE ---
  useEffect(() => {
    showUploadModalRef.current = showUploadModal;
  }, [showUploadModal]);
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);
  useEffect(() => {
    isStep3LoadingRef.current = isStep3Loading;
  }, [isStep3Loading]);
  useEffect(() => {
    step3DataRef.current = step3Data;
  }, [step3Data]);
  const fileInputRef = useRef(null);
  const step2SeededRef = useRef(false);

  // Helper to round to nearest 0.5
  const roundToHalfGlobal = (n) => Math.round(n * 2) / 2;

  /**
   * Subscribe to real-time updates for a given request ID.
   */
  const listenToEvaluation = useCallback(
    (requestId) => {
      if (!requestId) return;
      if (sseSubscriptionRef.current) {
        try {
          sseSubscriptionRef.current();
        } catch {
          // ignore stale unsubscribe errors
        }
        sseSubscriptionRef.current = null;
      }

      const unsubscribe = subscribeToWorksheet((event) => {
        if (!event || event.requestId !== requestId) {
          return;
        }

        if (event.type === "connected") {
          setIsSSEProcessing(true);
          setSseStatus("processing");
          return;
        }
        if (event.type === "evaluation_complete") {
          setSseProgress(100);
          setIsSSEProcessing(false);
          setIsStep3Loading(false);

          if (event.result !== undefined) {
            setStep3Data(event.result);
          }

          // IMPORTANT: mark as viewed only if results are visible in the modal
          queueMicrotask(() => {
            if (
              !hasMarkedViewedRef.current &&
              isResultsVisibleInModal() &&
              event.requestId
            ) {
              hasMarkedViewedRef.current = true;
              markWorksheetAsViewed(event.requestId);
            }
          });

          // close / cleanup as you already do
          try {
            eventSource.close?.();
          } catch {}
        }
        if (event.type === "status_update") {
          if (event.status === "completed") {
            setSseProgress(100);
            setIsSSEProcessing(false);
            setIsStep3Loading(false);

            if (event.result !== undefined) {
              setStep3Data(event.result);
            }

            // IMPORTANT: mark as viewed only if results are visible in the modal
            queueMicrotask(() => {
              if (
                !hasMarkedViewedRef.current &&
                isResultsVisibleInModal() &&
                event.requestId
              ) {
                hasMarkedViewedRef.current = true;
                markWorksheetAsViewed(event.requestId);
              }
            });

            // close / cleanup as you already do
            try {
              eventSource.close?.();
            } catch {}
          } else if (event.type === "failed") {
            setSseStatus("failed");
            setSseError(event.error || "Evaluation failed");
            setIsSSEProcessing(false);
            setIsStep3Loading(false);
            if (sseSubscriptionRef.current) {
              try {
                sseSubscriptionRef.current();
              } catch {
                // ignore
              }
              sseSubscriptionRef.current = null;
            }
          } else {
            // still running
            setIsSSEProcessing(true);
          }
          return;
        }

        if (event.type === "failed") {
          setSseStatus?.("completed");
          setSseProgress(100);
          setIsSSEProcessing(false);
          setIsStep3Loading(false);

          if (event.result !== undefined) {
            setStep3Data(event.result);
          }

          // Same conditional mark here
          queueMicrotask(() => {
            if (
              !hasMarkedViewedRef.current &&
              isResultsVisibleInModal() &&
              event.requestId
            ) {
              hasMarkedViewedRef.current = true;
              markWorksheetAsViewed(event.requestId);
            }
          });

          // close / cleanup as you already do
          try {
            eventSource.close?.();
          } catch {}
          return;
        }

        if (event.type === "evaluation_error") {
          setSseStatus("failed");
          setSseError(event.error || "Evaluation failed");
          setIsSSEProcessing(false);
          setIsStep3Loading(false);
          if (sseSubscriptionRef.current) {
            try {
              sseSubscriptionRef.current();
            } catch {
              // ignore
            }
            sseSubscriptionRef.current = null;
          }
        }
      });

      sseSubscriptionRef.current = unsubscribe;
    },
    [subscribeToWorksheet]
  );

  /**
   * Get evaluation status manually
   */
  const getEvaluationStatus = async (requestId) => {
    try {
      const status = await fetchWorksheetStatus(requestId);
      setSseStatus(status.status);
      setStep3Data(status.result);
      setSseError(status.error);
      return status;
    } catch (err) {
      setSseError(err.message);
      throw err;
    }
  };

  /**
   * Cancel current evaluation
   */
  const cancelEvaluation = useCallback(() => {
    const activeRequestId = currentRequestId || latestRequestIdRef.current;
    if (activeRequestId) {
      cancelWorksheetRequest(activeRequestId);
    }
    if (sseSubscriptionRef.current) {
      try {
        sseSubscriptionRef.current();
      } catch {
        // ignore
      }
      sseSubscriptionRef.current = null;
    }
    setIsSSEProcessing(false);
    setIsStep3Loading(false);
    setSseStatus("cancelled");
  }, [cancelWorksheetRequest, currentRequestId]);

  //TODO: Use API Call to get evaluated sheets
  //[{ id, fileName, date, overallMarks, totalMarks, questions: [{ id, questionImage, answerImages: [..], marks, totalMarks, feedback }] }]
  // useEffect(() => {
  //   async function load() {
  //     const res = await fetch('/api/evaluated-sheets');
  //     const data = await res.json();
  //     setEvaluatedSheets(data);
  //   }
  //   load();
  // }, []);

  // Normalize seeded evaluated sheets to 5 marks per question and recompute sheet totals
  // useEffect(() => {
  //   setEvaluatedSheets((current) => {
  //     return current.map((sheet) => {
  //       const normalizedQuestions = (sheet.questions || []).map((q) => {
  //         const base =
  //           q.totalMarks && q.totalMarks > 0 ? (q.marks / q.totalMarks) * 5 : 0;
  //         const marks5 = Math.max(0, Math.min(5, roundToHalfGlobal(base)));
  //         return { ...q, marks: marks5, totalMarks: 5 };
  //       });
  //       const overall = roundToHalfGlobal(
  //         normalizedQuestions.reduce((sum, q) => sum + (q.marks || 0), 0)
  //       );
  //       const total = (normalizedQuestions.length || 0) * 5;
  //       return {
  //         ...sheet,
  //         questions: normalizedQuestions,
  //         overallMarks: overall,
  //         totalMarks: total,
  //       };
  //     });
  //   });
  // }, []);

  // Handle upload new answer sheet
  const handleUploadNew = () => {
    setShowUploadModal(true);
    // reset multistep flow each time
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setStep1QuestionImages([]);
    setStep1AnswerImages([]);
    setStep1CombinedImages([]);
    setStep1CombinedThumbnails(new Map());
    setStep1UploadOption("separate");
    setIsAnswerSheetMarked(false);
    setSelectedWorksheet(null);
    setVerifiedQuestions(new Set());
    setPreviewSheet(null);
    setStep2ExpandedQuestions(new Set([1]));
    setStep3ExpandedQuestions(new Set([1]));
    step2SeededRef.current = false;
    setUploadData({
      questions: [
        {
          id: 1,
          questionImage: null,
          answerImages: [],
          marks: 0,
        },
      ],
    });

    // Reset SSE state
    setIsSSEProcessing(false);
    setSseProgress(0);
    setSseStatus("idle");
    setSseError(null);
    setCurrentRequestId(null);
    if (sseSubscriptionRef.current) {
      try {
        sseSubscriptionRef.current();
      } catch {
        // ignore
      }
      sseSubscriptionRef.current = null;
    }
  };

  // Check for ?mode=new query parameter on mount and trigger handleUploadNew
  useEffect(() => {
    if (typeof window === "undefined") return;

    const searchParams = new URLSearchParams(window.location.search);
    const mode = searchParams.get("mode");

    if (mode === "new") {
      handleUploadNew();
      // Clean up the query parameter from URL after triggering
      searchParams.delete("mode");
      const newSearch = searchParams.toString();
      const newUrl =
        window.location.pathname +
        (newSearch ? `?${newSearch}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark worksheet as viewed with retry logic
  const markWorksheetAsViewed = async (requestId, retryCount = 0) => {
    const maxRetries = 3;

    try {
      const baseUrl = import.meta.env.VITE_APP_BASE_URL;
      if (!baseUrl) {
        console.error("Base URL not configured for mark-viewed API");
        return;
      }

      const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
      const markViewedEndpoint = `${trimmedBaseUrl}/v2/worksheetEvaluation/mark-viewed/${requestId}`;

      // Get auth token from localStorage or sessionStorage
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      const response = await fetch(markViewedEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log(`Successfully marked worksheet ${requestId} as viewed`);
    } catch (error) {
      console.error(
        `Failed to mark worksheet as viewed (attempt ${retryCount + 1}):`,
        error
      );

      if (retryCount < maxRetries - 1) {
        // Wait before retry (exponential backoff)
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          markWorksheetAsViewed(requestId, retryCount + 1);
        }, delay);
      } else {
        console.error(
          `Failed to mark worksheet ${requestId} as viewed after ${maxRetries} attempts`
        );
      }
    }
  };

  // Handle viewing evaluation results
  const handleViewResults = (sheet) => {
    setSelectedSheet(sheet);
    const firstQuestionId =
      Array.isArray(sheet?.questions) && sheet.questions.length > 0
        ? sheet.questions[0]?.id ?? 1
        : null;
    setExpandedQuestions(
      firstQuestionId !== null ? new Set([firstQuestionId]) : new Set()
    );
    setShowSidePanel(false);
    setShowResultsModal(true);
    if (sheet?.requestId) {
      applyResultsModalUrl(sheet.requestId);
    }

    // Mark as viewed if requestId is available and sheet is unviewed
    if (sheet?.requestId && sheet?.viewed === false) {
      markWorksheetAsViewed(sheet.requestId);
    }
  };

  // Handle image preview
  const handleImagePreview = (imageSrc, imageAlt, images = null, index = 0) => {
    setPreviewImage(imageSrc);
    setPreviewImageAlt(imageAlt);
    if (Array.isArray(images) && images.length > 0) {
      // normalize to objects {src,isPdf}
      const normalized = images.map((it) => {
        if (typeof it === "string") return { src: it, isPdf: false };
        if (it && typeof it === "object" && typeof it.src === "string")
          return it;
        return { src: String(it || ""), isPdf: false };
      });
      setPreviewImages(normalized);
      setPreviewIndex(index);
    } else {
      setPreviewImages([{ src: imageSrc, isPdf: false }]);
      setPreviewIndex(0);
    }
    setShowImagePreview(true);
  };

  const goPrevPreview = () => {
    if (previewImages.length <= 1) return;
    setPreviewIndex(
      (prev) => (prev - 1 + previewImages.length) % previewImages.length
    );
  };

  const goNextPreview = () => {
    if (previewImages.length <= 1) return;
    setPreviewIndex((prev) => (prev + 1) % previewImages.length);
  };

  useEffect(() => {
    if (!showImagePreview) return;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrevPreview();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNextPreview();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowImagePreview(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showImagePreview, previewImages.length]);

  // keep displayed image in sync with index
  useEffect(() => {
    if (!showImagePreview) return;
    if (previewImages.length === 0) return;
    const next = previewImages[previewIndex % previewImages.length];
    if (next && next.src && next.src !== previewImage) {
      setPreviewImage(next.src);
    }
  }, [previewIndex, previewImages, showImagePreview]);

  const renderLeftUtilityPanel = (leftInfoPanelWidth) => {
    const offlinePreviewActive =
      showUploadModal &&
      currentStep === 3 &&
      offlineUploadPreviewAssets &&
      ((typeof offlineUploadPreviewAssets?.pdfUrl === "string" &&
        offlineUploadPreviewAssets.pdfUrl.length > 0) ||
        (Array.isArray(offlineUploadPreviewAssets?.imageUrls) &&
          offlineUploadPreviewAssets.imageUrls.length > 0));

    const pdfCandidate = offlinePreviewActive
      ? offlineUploadPreviewAssets?.pdfUrl
      : leftPanelViewer?.pdfUrl;
    const pdfUrl =
      typeof pdfCandidate === "string" && pdfCandidate.trim().length > 0
        ? pdfCandidate.trim()
        : null;

    const baseImageEntries = offlinePreviewActive
      ? Array.isArray(offlineUploadPreviewAssets?.imageUrls)
        ? offlineUploadPreviewAssets.imageUrls
        : []
      : Array.isArray(leftPanelViewer?.imageUrls)
      ? leftPanelViewer.imageUrls
      : [];

    const imageEntries = baseImageEntries
      .map((entry) => {
        if (typeof entry === "string") {
          const trimmed = entry.trim();
          return trimmed.length > 0 ? { src: trimmed, label: "" } : null;
        }
        if (
          entry &&
          typeof entry === "object" &&
          typeof entry.src === "string"
        ) {
          return {
            src: entry.src.trim(),
            label: typeof entry.label === "string" ? entry.label.trim() : "",
          };
        }
        return null;
      })
      .filter((entry) => entry && entry.src.length > 0);
    const hasImages = imageEntries.length > 0;

    const renderMessage = (message, isError = false) => (
      <div className="flex flex-1 items-center justify-center p-4 text-center text-sm">
        <span className={isError ? "text-red-500" : "text-gray-500"}>
          {message}
        </span>
      </div>
    );

    const openPdfPreview = () => {
      if (!pdfUrl) return;
      handleImagePreview(
        pdfUrl,
        "Worksheet PDF",
        [{ src: pdfUrl, isPdf: true }],
        0
      );
    };

    const renderPdfPreview = () => (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
          <button
            type="button"
            onClick={openPdfPreview}
            className="text-xs font-semibold text-[#0D99FF] hover:text-[#5d4de2]"
          >
            Open
          </button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-hidden">
          <iframe
            src={pdfUrl}
            title="Worksheet PDF preview"
            className="w-full h-full border-0"
          />
        </div>
      </div>
    );

    const renderImagesPreview = () => {
      const previewSources = imageEntries.map((entry) => entry.src);
      return (
        <div className="flex flex-col h-full overflow-hidden bg-white">
          <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-700">
            <FaImage className="text-[#0D99FF]" />
            <span>Your Answers</span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 p-3 bg-white">
            {imageEntries.map(({ src, label }, index) => (
              <button
                key={`${index}-${src}`}
                type="button"
                onClick={() =>
                  handleImagePreview(
                    src,
                    label || `Worksheet page ${index + 1}`,
                    previewSources,
                    index
                  )
                }
                className="w-full border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white text-left"
              >
                <img
                  src={src}
                  alt={`Worksheet page ${index + 1}`}
                  className="w-full h-48 object-contain bg-gray-50"
                  onError={(e) => {
                    e.currentTarget.src = "/images/avatar1.png";
                  }}
                />
                <div className="px-3 py-2 text-xs text-gray-600 border-t border-gray-100">
                  Page {index + 1}
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    };

    let body = null;
    if (offlinePreviewActive) {
      if (pdfUrl) {
        body = renderPdfPreview();
      } else if (hasImages) {
        body = renderImagesPreview();
      } else {
        body = renderMessage(
          "Upload your worksheet files to preview them here."
        );
      }
    } else if (!showResultsModal || !selectedSheetRequestId) {
      body = renderMessage("Open evaluation results to preview the worksheet.");
    } else if (isLeftPanelViewerLoading) {
      body = (
        <div className="flex flex-1 items-center justify-center">
          <SimpleThreeDotsLoader message="Loading worksheet file..." />
        </div>
      );
    } else if (leftPanelViewerError) {
      body = renderMessage(leftPanelViewerError, true);
    } else if (pdfUrl) {
      body = renderPdfPreview();
    } else if (hasImages) {
      body = renderImagesPreview();
    } else {
      body = renderMessage("No file available for this evaluation.");
    }

    return (
      <div
        className=" h-full overflow-hidden border-r border-gray-200 bg-white flex flex-col"
        style={{
          width: `${leftInfoPanelWidth}%`,
          pointerEvents: isLeftResizing ? "none" : undefined,
        }}
      >
        {body}
      </div>
    );
  };

  // Handle question toggle
  const toggleQuestion = (questionId) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Handle file upload for answer images
  const handleAnswerImageUpload = (questionId, e) => {
    const files = Array.from(e.target.files);
    setStep2Questions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, answerImages: [...q.answerImages, ...files] }
          : q
      )
    );
  };

  // Add new question row
  const addNewQuestion = () => {
    const newId =
      (step2Questions.length
        ? Math.max(...step2Questions.map((q) => q.id))
        : 0) + 1;
    setStep2Questions((prev) => [
      ...prev,
      { id: newId, questionImage: null, answerImages: [], marks: 5 },
    ]);
  };

  // Remove question row
  const removeQuestion = (questionId) => {
    if (step2Questions.length > 1) {
      setStep2Questions((prev) => prev.filter((q) => q.id !== questionId));
    }
  };

  // Remove answer image
  const removeAnswerImage = (questionId, imageIndex) => {
    setStep2Questions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? {
              ...q,
              answerImages: q.answerImages.filter(
                (_, index) => index !== imageIndex
              ),
            }
          : q
      )
    );
  };

  // Multistep: step transitions (no going back to completed steps)
  const goToNextStep = () => {
    setCompletedSteps((prev) => new Set(prev).add(currentStep));
    setCurrentStep((prev) => Math.min(3, prev + 1));
  };

  // Check if all questions are verified
  const areAllQuestionsVerified = () => {
    return (
      step2Questions.length > 0 &&
      verifiedQuestions.size === step2Questions.length
    );
  };

  // Handle Process for Evaluation button click
  const handleProcessForEvaluation = () => {
    // While Step 2 is disabled, go directly from Step 1 → Step 3 using dummy data
    if (STEP2_DISABLED && currentStep === 1) {
      try {
        // Seed step 3 with existing dummy evaluated sheet
        const dummy = evaluatedSheets[0];
        if (!dummy) {
          toast.error(
            "No evaluated sheets available yet. Please try again after an evaluation completes."
          );
          return;
        }
        setStep1ApiResponse(dummy);
        setStep3Data(dummy);
        setIsStep3Loading(true);
        setCompletedSteps((prev) => new Set(prev).add(1));
        setCurrentStep(3);
        setTimeout(() => setIsStep3Loading(false), 4000);
      } catch (e) {
        console.error("Failed to prepare preview data:", e);
      }
      return;
    }
    // Original Step 2 flow retained (will be used when STEP2_DISABLED=false)
    if (areAllQuestionsVerified()) {
      // All questions verified, proceed directly to API call
      handleStep2Submit();
    } else {
      // Not all questions verified, show confirmation modal
      setShowConfirmationModal(true);
    }
  };
  const isResultsVisibleInModal = () => {
    // Results are considered "seen" when:
    // - Upload/Evaluate modal is open
    // - We are in Step 3 (results step)
    // - We have result data and not loading
    return (
      showUploadModalRef.current === true &&
      currentStepRef.current === 3 &&
      !!step3DataRef.current &&
      isStep3LoadingRef.current === false
    );
  };

  // New function to upload student answers using FormData with SSE
  const uploadStudentAnswers = async () => {
    try {
      hasMarkedViewedRef.current = false;
      setIsUploading(true);
      setIsStep3Loading(true);
      setSseError(null);
      setSseStatus("submitting");
      setCurrentStep(3);
      setCompletedSteps((prev) => new Set(prev).add(1));

      const fd = new FormData();

      // Add files based on upload option
      if (step1UploadOption === "separate") {
        // Append multiple files under the SAME field name
        for (const f of step1QuestionImages) fd.append("question_images", f);
        for (const f of step1AnswerImages) fd.append("answer_images", f);
      } else if (step1UploadOption === "combined") {
        // Add combined images/PDFs
        for (const f of step1CombinedImages) fd.append("combined_images", f);
      } else if (step1UploadOption === "worksheet" && selectedWorksheet) {
        // Add worksheet ID and answer images
        fd.append("worksheet_id", selectedWorksheet.id.toString());
        for (const f of step1AnswerImages) fd.append("answer_images", f);
      }

      // Other simple fields
      fd.append("timestamp", new Date().toISOString());
      fd.append("upload_option", step1UploadOption);
      fd.append("is_answer_sheet_marked", isAnswerSheetMarked.toString());

      if (step1UploadOption === "separate") {
        fd.append("total_questions", String(step1QuestionImages.length));
        fd.append("total_answers", String(step1AnswerImages.length));
      } else if (step1UploadOption === "combined") {
        fd.append("total_combined", String(step1CombinedImages.length));
      } else if (step1UploadOption === "worksheet") {
        fd.append("worksheet_title", selectedWorksheet?.title || "");
        fd.append("worksheet_subject", selectedWorksheet?.subject || "");
        fd.append("worksheet_topic", selectedWorksheet?.topic || "");
        fd.append(
          "worksheet_question_count",
          selectedWorksheet?.questionCount?.toString() || "0"
        );
        fd.append("total_answers", String(step1AnswerImages.length));
      }

      const submission = await submitWithFormData({ formData: fd });
      console.log("Submission response:", submission);
      if (submission?.rateLimit) {
        const message = submission.message || RATE_LIMIT_FALLBACK_MESSAGE;
        toast.warn(message);
        showRateLimitModal(message);
        setSseStatus("failed");
        setIsSSEProcessing(false);
        setIsStep3Loading(false);
        setIsUploading(false);
        return;
      }
      const { requestId } = submission || {};
      if (!requestId) {
        throw new Error("No requestId returned from submit API.");
      }

      console.log("Submission successful:", submission);

      // Store the request ID and start listening for updates
      setCurrentRequestId(requestId);
      setSseStatus("submitted");
      setSseProgress(0);
      setIsSSEProcessing(true);
      listenToEvaluation(requestId);

      // Store the response data for use in subsequent steps
      setStep1ApiResponse(submission);
    } catch (error) {
      console.error("Upload failed:", error);
      setSseError(error.message);
      setSseStatus("failed");
      setIsStep3Loading(false);
      setIsUploading(false);
      markWorksheetAsViewed(requestId);
      const rateLimitMessage = deriveRateLimitMessageFromError(error);
      if (rateLimitMessage) {
        toast.warn(rateLimitMessage);
        showRateLimitModal(rateLimitMessage);
      } else {
        toast.error(`Upload failed: ${error.message}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Handle Step 2 submission to API
  const handleStep2Submit = async () => {
    try {
      // Show loading state
      // toast.info("Processing evaluation data...");
      setIsStep3Loading(true);

      // Use the complete step1ApiResponse (dummyData) and only update marks from step2Questions
      const step3dummyData = {
        ...step1ApiResponse,
      };

      // Store the generated Step 3 data
      setStep3Data(step3dummyData);

      // Show loader for 3 seconds before rendering Step 3 content

      // Move to Step 3
      setCompletedSteps((prev) => new Set(prev).add(2));
      setCurrentStep(3);
      setTimeout(() => {
        setIsStep3Loading(false);
      }, 6000);
      // Prepare data for API call using current step2Questions state
      const step2Data = {
        id: step1ApiResponse?.id,
        questions: step2Questions.map((q) => ({
          id: q.id,
          questionImage: q.questionImage,
          answerImages: q.answerImages,
          marks: q.marks,
          totalMarks: q.totalMarks,
        })),
        verifiedQuestions: Array.from(verifiedQuestions),
        timestamp: new Date().toISOString(),
      };
      console.log("Step 2 data:", step2Data);
      // Get base URL from environment
      const baseUrl = import.meta.env.VITE_APP_BASE_URL;
      if (!baseUrl) {
        throw new Error("Base URL not configured");
      }

      // Send to backend
      const response = await fetch(`${baseUrl}/postStep2Data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(step2Data),
      });

      const rateLimitMessage = await extractRateLimitMessageFromResponse(
        response
      );
      if (rateLimitMessage) {
        toast.warn(rateLimitMessage);
        setIsStep3Loading(false);
        showRateLimitModal(rateLimitMessage);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      // Show success message
      toast.success("Evaluation data processed successfully!");

      setIsStep3Loading(true);

      // Store the generated Step 3 data
      setStep3Data(result);

      // Move to Step 3
      setCompletedSteps((prev) => new Set(prev).add(2));
      setCurrentStep(3);

      console.log("Step 2 API response:", result);
      console.log("Generated Step 3 data:", step3Data);
    } catch (error) {
      console.error("Error processing Step 2 data:", error);
      const rateLimitMessage = deriveRateLimitMessageFromError(error);
      if (rateLimitMessage) {
        toast.warn(rateLimitMessage);
        showRateLimitModal(rateLimitMessage);
      } else {
        // toast.error(
        //   "An error occurred while processing evaluation data. Please try again."
        // );
      }
    }
  };

  // Handle confirmation modal responses
  const handleConfirmationYes = () => {
    setShowConfirmationModal(false);
    handleStep2Submit();
  };

  const handleConfirmationNo = () => {
    setShowConfirmationModal(false);
  };

  // Step 1 to Step 2: Collect data and send to backend
  const goToStep2 = async () => {
    setIsStep2Loading(true);
    // Generate varied response JSON for Step 2 using actual images from public folder

    if (isUploading) {
      setIsStep2Loading(false);
      return; // Prevent multiple submissions
    }

    const fallbackSheet = evaluatedSheets[0];
    if (!fallbackSheet) {
      toast.error(
        "No evaluated sheets available yet. Please upload and evaluate a sheet before proceeding."
      );
      setIsStep2Loading(false);
      setIsUploading(false);
      return;
    }

    // Store the dummy data as step1ApiResponse
    setStep1ApiResponse(fallbackSheet);
    setTimeout(() => {
      setIsStep2Loading(false);
      setIsUploading(false);
    }, 3000);
    // Populate step2Questions with the dummy data
    const questions = (fallbackSheet.questions || []).map((q, index) => ({
      id: q.id || index + 1,
      questionImage: q.questionImage || "/images/question_example.png",
      answerImages: q.answerImages || [],
      marks: q.marks || 5,
      totalMarks: q.totalMarks || 5,
      feedback: q.feedback || "",
      stepBreakdown: q.stepBreakdown || [],
      question_text: q.question_text || "",
    }));
    setStep2Questions(questions);
    toast.success("Files uploaded successfully! Processing evaluation...");
    setIsUploading(false);
    setCompletedSteps((prev) => new Set(prev).add(currentStep));
    setCurrentStep((prev) => Math.min(3, prev + 1));

    try {
      setIsUploading(true);

      // Show loading state
      // toast.info("Processing uploaded files...");

      // Create FormData to send files to backend
      const formData = new FormData();

      // Add files based on upload option
      if (step1UploadOption === "separate") {
        // Add question images/PDFs with unique names
        step1QuestionImages.forEach((file, index) => {
          formData.append(`question_images[${index}]`, file);
        });

        // Add answer images with unique names
        step1AnswerImages.forEach((file, index) => {
          formData.append(`answer_images[${index}]`, file);
        });
      } else if (step1UploadOption === "combined") {
        // Add combined images/PDFs
        step1CombinedImages.forEach((file, index) => {
          formData.append(`combined_images[${index}]`, file);
        });
      } else if (step1UploadOption === "worksheet" && selectedWorksheet) {
        // Add worksheet ID and answer images
        formData.append("worksheet_id", selectedWorksheet.id.toString());
        step1AnswerImages.forEach((file, index) => {
          formData.append(`answer_images[${index}]`, file);
        });
      }

      // Add metadata
      formData.append("timestamp", new Date().toISOString());
      formData.append("upload_option", step1UploadOption);
      formData.append("is_answer_sheet_marked", isAnswerSheetMarked.toString());

      if (step1UploadOption === "separate") {
        formData.append(
          "total_questions",
          step1QuestionImages.length.toString()
        );
        formData.append("total_answers", step1AnswerImages.length.toString());
      } else if (step1UploadOption === "combined") {
        formData.append(
          "total_combined",
          step1CombinedImages.length.toString()
        );
      } else if (step1UploadOption === "worksheet") {
        formData.append("worksheet_title", selectedWorksheet?.title || "");
        formData.append("worksheet_subject", selectedWorksheet?.subject || "");
        formData.append("worksheet_topic", selectedWorksheet?.topic || "");
        formData.append(
          "worksheet_question_count",
          selectedWorksheet?.questionCount?.toString() || "0"
        );
        formData.append("total_answers", step1AnswerImages.length.toString());
      }

      // Get base URL from environment
      const baseUrl = import.meta.env.VITE_APP_BASE_URL;
      if (!baseUrl) {
        throw new Error("Base URL not configured");
      }

      // Send to backend
      const response = await fetch(`${baseUrl}/postEvaluateAnswer`, {
        method: "POST",
        body: formData,
        // Headers will be automatically added by the fetch interceptor for auth
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      // Show success message
      toast.success("Files uploaded successfully! Processing evaluation...");

      // Store the response data for use in subsequent steps
      console.log("Backend response:", result);
      setStep1ApiResponse(result);

      // Populate step2Questions with API response
      if (result.questions && Array.isArray(result.questions)) {
        const questions = result.questions.map((q, index) => ({
          id: q.id || index + 1,
          questionImage: q.questionImage || "/images/question_example.png",
          answerImages: q.answerImages || [],
          marks: q.marks || 5,
          totalMarks: q.totalMarks || 5,
          feedback: q.feedback || "",
          stepBreakdown: q.stepBreakdown || [],
        }));
        setStep2Questions(questions);
      }

      // Move to next step after successful upload
      setCompletedSteps((prev) => new Set(prev).add(currentStep));
      setCurrentStep((prev) => Math.min(3, prev + 1));
    } catch (error) {
      console.error("Error uploading files:", error);
      // toast.error("An error occurred while uploading files. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const isPdfFile = (file) => {
    if (!file) return false;
    const type = typeof file.type === "string" ? file.type.toLowerCase() : "";
    const name = typeof file.name === "string" ? file.name.toLowerCase() : "";
    return type.includes("pdf") || name.endsWith(".pdf");
  };

  const filterStep1Uploads = (files, existingFiles, areaLabel) => {
    if (!files.length) return [];

    const existingHasPdf = (existingFiles || []).some((file) =>
      isPdfFile(file)
    );
    const existingHasImages = (existingFiles || []).some(
      (file) => file && !isPdfFile(file)
    );

    let pdfFiles = files.filter((file) => isPdfFile(file));
    const imageFiles = files.filter((file) => !isPdfFile(file));

    if (existingHasPdf) {
      toast.error(
        `A PDF is already uploaded for the ${areaLabel}. Remove it before uploading more files.`
      );
      return [];
    }

    if (pdfFiles.length > 0 && existingHasImages) {
      toast.error(
        `You can only upload images for the ${areaLabel}. Remove them first to upload a PDF.`
      );
      return imageFiles;
    }

    if (pdfFiles.length > 0 && imageFiles.length > 0) {
      toast.error(
        `Please upload either images or a single PDF for the ${areaLabel}, not both at once.`
      );
      return [];
    }

    if (pdfFiles.length > 1) {
      toast.error(`Only one PDF is allowed for the ${areaLabel}.`);
      pdfFiles = pdfFiles.slice(0, 1);
    }

    if (pdfFiles.length > 0) {
      return pdfFiles;
    }

    return imageFiles;
  };

  // Step 1 handlers: bulk uploads and previews
  const handleStep1QuestionUpload = async (e) => {
    const files = Array.from(e.target?.files || []);
    if (!files.length) {
      if (e?.target && "value" in e.target) e.target.value = "";
      return;
    }

    const acceptedFiles = filterStep1Uploads(
      files,
      step1QuestionImages,
      "questions"
    );

    if (!acceptedFiles.length) {
      if (e?.target && "value" in e.target) e.target.value = "";
      return;
    }

    setStep1QuestionImages((prev) => [...prev, ...acceptedFiles]);

    const pdfFiles = acceptedFiles.filter(isPdfFile);

    if (pdfFiles.length > 0) {
      try {
        const thumbnailPromises = pdfFiles.map(async (file) => {
          try {
            const thumbnail = await generatePdfThumbnail(file, {
              width: 200,
              height: 200,
            });
            return { file, thumbnail };
          } catch (error) {
            console.error(
              `Failed to generate thumbnail for ${file.name}:`,
              error
            );
            return { file, thumbnail: null };
          }
        });

        const results = await Promise.all(thumbnailPromises);

        setStep1QuestionThumbnails((prev) => {
          const newMap = new Map(prev);
          results.forEach(({ file, thumbnail }) => {
            if (thumbnail) {
              newMap.set(file, thumbnail);
            }
          });
          return newMap;
        });
      } catch (error) {
        console.error("Error generating PDF thumbnails:", error);
        toast.error("Some PDF thumbnails could not be generated");
      }
    }

    if (e?.target && "value" in e.target) e.target.value = "";
  };

  const handleStep1AnswerUpload = async (e) => {
    const files = Array.from(e.target?.files || []);
    if (!files.length) {
      if (e?.target && "value" in e.target) e.target.value = "";
      return;
    }

    const acceptedFiles = filterStep1Uploads(
      files,
      step1AnswerImages,
      "answers"
    );

    if (!acceptedFiles.length) {
      if (e?.target && "value" in e.target) e.target.value = "";
      return;
    }

    setStep1AnswerImages((prev) => [...prev, ...acceptedFiles]);

    const pdfFiles = acceptedFiles.filter(isPdfFile);

    if (pdfFiles.length > 0) {
      try {
        const thumbnailPromises = pdfFiles.map(async (file) => {
          try {
            const thumbnail = await generatePdfThumbnail(file, {
              width: 200,
              height: 200,
            });
            return { file, thumbnail };
          } catch (error) {
            console.error(
              `Failed to generate thumbnail for ${file.name}:`,
              error
            );
            return { file, thumbnail: null };
          }
        });

        const results = await Promise.all(thumbnailPromises);

        setStep1AnswerThumbnails((prev) => {
          const newMap = new Map(prev);
          results.forEach(({ file, thumbnail }) => {
            if (thumbnail) {
              newMap.set(file, thumbnail);
            }
          });
          return newMap;
        });
      } catch (error) {
        console.error("Error generating PDF thumbnails:", error);
        toast.error("Some PDF thumbnails could not be generated");
      }
    }

    if (e?.target && "value" in e.target) e.target.value = "";
  };

  // New handlers for enhanced Step 1 options
  const handleStep1CombinedUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setStep1CombinedImages((prev) => [...prev, ...files]);

      // Generate thumbnails for PDF files
      const pdfFiles = files.filter(
        (file) =>
          (file.type && file.type.includes("pdf")) ||
          (file.name && file.name.toLowerCase().endsWith(".pdf"))
      );

      if (pdfFiles.length > 0) {
        try {
          const thumbnailPromises = pdfFiles.map(async (file) => {
            try {
              const thumbnail = await generatePdfThumbnail(file, {
                width: 200,
                height: 200,
              });
              return { file, thumbnail };
            } catch (error) {
              console.error(
                `Failed to generate thumbnail for ${file.name}:`,
                error
              );
              return { file, thumbnail: null };
            }
          });

          const results = await Promise.all(thumbnailPromises);

          setStep1CombinedThumbnails((prev) => {
            const newMap = new Map(prev);
            results.forEach(({ file, thumbnail }) => {
              if (thumbnail) {
                newMap.set(file, thumbnail);
              }
            });
            return newMap;
          });
        } catch (error) {
          console.error("Error generating PDF thumbnails:", error);
          toast.error("Some PDF thumbnails could not be generated");
        }
      }
    }
  };

  const removeStep1CombinedImage = (idx) => {
    setStep1CombinedImages((prev) => {
      const newFiles = [...prev];
      const removedFile = newFiles.splice(idx, 1)[0];

      // Clean up thumbnail
      setStep1CombinedThumbnails((prevThumbs) => {
        const newMap = new Map(prevThumbs);
        newMap.delete(removedFile);
        return newMap;
      });

      return newFiles;
    });
  };

  const handleWorksheetSelection = (worksheet) => {
    setSelectedWorksheet(worksheet);
  };
  const removeStep1QuestionImage = (idx) => {
    setStep1QuestionImages((prev) => {
      const newImages = prev.filter((_, i) => i !== idx);
      const removedFile = prev[idx];

      // Remove thumbnail if it exists
      if (removedFile) {
        setStep1QuestionThumbnails((prevThumbs) => {
          const newMap = new Map(prevThumbs);
          newMap.delete(removedFile);
          return newMap;
        });
      }

      return newImages;
    });
  };
  const removeStep1AnswerImage = (idx) => {
    setStep1AnswerImages((prev) => {
      const newImages = prev.filter((_, i) => i !== idx);
      const removedFile = prev[idx];

      if (removedFile) {
        setStep1AnswerThumbnails((prevThumbs) => {
          const newMap = new Map(prevThumbs);
          newMap.delete(removedFile);
          return newMap;
        });
      }

      return newImages;
    });
  };

  const setDropZoneHoverState = (type, value) => {
    setDropZoneHover((prev) => {
      if (prev[type] === value) return prev;
      return { ...prev, [type]: value };
    });
  };

  const handleDropZoneDragEnter = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneHoverState(type, true);
  };

  const handleDropZoneDragLeave = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.currentTarget &&
      e.relatedTarget &&
      e.currentTarget.contains(e.relatedTarget)
    ) {
      return;
    }
    setDropZoneHoverState(type, false);
  };

  const handleDropZoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropZoneDrop = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneHoverState(type, false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    const syntheticEvent = { target: { files } };
    if (type === "question") {
      await handleStep1QuestionUpload(syntheticEvent);
    } else if (type === "combined") {
      await handleStep1CombinedUpload(syntheticEvent);
    } else if (type === "answer" || type === "worksheetAnswer") {
      await handleStep1AnswerUpload(syntheticEvent);
    }
  };

  useEffect(() => {
    if (!showUploadModal || currentStep !== 3) {
      setOfflineUploadPreviewAssets(null);
      return;
    }

    const sections = [
      { files: step1CombinedImages, label: "Combined" },
      { files: step1QuestionImages, label: "Question" },
      { files: step1AnswerImages, label: "Answer" },
    ];

    if (!sections.some(({ files }) => files.length > 0)) {
      setOfflineUploadPreviewAssets(null);
      return;
    }

    const createdUrls = [];
    const createObjectUrl = (file) => {
      const url = URL.createObjectURL(file);
      createdUrls.push(url);
      return url;
    };

    let assets = null;

    for (const section of sections) {
      const pdfFile = section.files.find((file) => isPdfFile(file));
      if (pdfFile) {
        assets = {
          pdfUrl: createObjectUrl(pdfFile),
          imageUrls: [],
        };
        break;
      }
    }

    if (!assets) {
      const imageEntries = [];
      sections.forEach(({ files, label }) => {
        files.forEach((file, index) => {
          if (!isPdfFile(file)) {
            imageEntries.push({
              src: createObjectUrl(file),
              label: `${label} ${index + 1}`,
            });
          }
        });
      });
      if (imageEntries.length > 0) {
        assets = {
          pdfUrl: null,
          imageUrls: imageEntries,
        };
      }
    }

    setOfflineUploadPreviewAssets(assets);

    return () => {
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [
    showUploadModal,
    currentStep,
    step1CombinedImages,
    step1QuestionImages,
    step1AnswerImages,
  ]);

  // Build Step 2 rows from Step 1 (prefill placeholders, maintain add/remove row features)
  //TODO: Use API to get questions and answers for step 2 of evaluate answersheet
  //JSON response structure is : [{ id, questionImage, answerImages, marks, totalMarks, feedback }]
  const initStep2FromStep1 = () => {
    // Only create dummy data if no API response is available
    if (step1ApiResponse && step1ApiResponse.questions) {
      return; // API response already populated step2Questions
    }

    const count = 5; // always show 5 rows by default
    const questions = Array.from({ length: count }).map((_, i) => ({
      id: i + 1,
      // prefill placeholders as requested
      questionImage: "/images/question_example.png",
      // 2-3 dummy placeholders per request
      answerImages: Array.from({ length: i % 2 === 0 ? 2 : 3 }).map(
        () => "/images/answer_sheet_example.png"
      ),
      marks: 5,
    }));
    setStep2Questions(questions);
    // Only first question opened initially
    setStep2ExpandedQuestions(new Set([1]));
  };

  // Entering Step 2: initialize seeded rows exactly once
  // useEffect(() => {
  //   if (!showUploadModal) return;
  //   if (currentStep !== 2) return;
  //   if (step2SeededRef.current) return;
  //   initStep2FromStep1();
  //   step2SeededRef.current = true;
  // }, [currentStep, showUploadModal]);

  // Verify controls
  const toggleVerifyQuestion = (questionId) => {
    setVerifiedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };
  const verifyAllQuestions = () => {
    const allIds = step2Questions.map((q) => q.id);
    setVerifiedQuestions(new Set(allIds));
  };

  // Step 3 preview seed (independent of Step 2, easy to replace via API)
  // const seedStep3Data = () => {
  //   const count = 5;
  //   const questions = Array.from({ length: count }).map((_, i) => {
  //     const totalMarks = 5;
  //     const marks = roundToHalfGlobal(Math.random() * 5);
  //     // Build step-by-step breakdown from total marks using caps [2, 1.5, 1.5]
  //     const caps = [2, 1.5, 1.5];
  //     let remaining = Math.max(0, Math.min(totalMarks, marks));
  //     const stepBreakdown = caps.map((cap, idx) => {
  //       const ideal = remaining / (caps.length - idx);
  //       let awarded = Math.min(cap, ideal);
  //       awarded = roundToHalfGlobal(awarded);
  //       if (awarded > remaining)
  //         awarded = roundToHalfGlobal(Math.max(0, remaining));
  //       awarded = Math.min(cap, awarded);
  //       remaining = Math.max(0, roundToHalfGlobal(remaining - awarded));
  //       return {
  //         step: idx + 1,
  //         feedback: `Preview feedback for step ${
  //           idx + 1
  //         } describing strengths and areas to improve.`,
  //         marks: awarded,
  //         maxMarks: cap,
  //       };
  //     });
  //     return {
  //       id: i + 1,
  //       questionImage: "/images/question_example.png",
  //       answerImages: Array.from({ length: i % 2 === 0 ? 3 : 2 }).map(
  //         () => "/images/answer_sheet_example.png"
  //       ),
  //       marks,
  //       totalMarks,
  //       feedback: `Question ${i + 1}: Auto-generated preview feedback.`,
  //       extractedContent: {
  //         problem:
  //           "Preview problem statement extracted from question image (sample placeholder).",
  //       },
  //       stepBreakdown,
  //     };
  //   });
  //   const overall5 = roundToHalfGlobal(
  //     questions.reduce((s, q) => s + q.marks, 0)
  //   );
  //   const total5 = questions.length * 5;
  //   setStep3Data({
  //     id: "step3-preview",
  //     fileName: "Preview_Answer_Sheet.pdf",
  //     thumbnail: getSubjectIcon("General"),
  //     subject: "General",
  //     overallMarks: overall5,
  //     totalMarks: total5,
  //     date: new Date().toISOString().split("T")[0],
  //     questions,
  //   });
  // };

  // useEffect(() => {
  //   if (currentStep === 3 && !step3Data) {
  //     seedStep3Data();
  //   }
  // }, [currentStep, step3Data]);

  // Toggle collapsible helpers for Step 2 and Step 3
  const toggleStep2Question = (questionId) => {
    setStep2ExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };
  const toggleStep3Question = (questionId) => {
    setStep3ExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  // Handle save and process
  // const handleSaveAndProcess = async () => {
  //   if (uploadData.questions.some((q) => !q.questionImage)) {
  //     toast.error("Please upload question images for all questions");
  //     return;
  //   }

  //   if (uploadData.questions.some((q) => q.answerImages.length === 0)) {
  //     toast.error("Please upload answer images for all questions");
  //     return;
  //   }

  //   if (uploadData.questions.some((q) => !q.marks || q.marks <= 0)) {
  //     toast.error("Please enter marks for all questions");
  //     return;
  //   }

  //   setIsProcessing(true);
  //   setProcessingProgress(0);
  //   setShowUploadModal(false);

  //   // Simulate processing with progress
  //   const interval = setInterval(() => {
  //     setProcessingProgress((prev) => {
  //       if (prev >= 100) {
  //         clearInterval(interval);
  //         setIsProcessing(false);
  //         // Add new evaluated sheet to the list
  //         // Create a new sheet with 5 marks per question, .5 granularity
  //         const generatedQuestions = uploadData.questions.map((q, index) => {
  //           const randomHalfMark = roundToHalfGlobal(Math.random() * 5); // 0 to 5 in .5 steps
  //           const caps = [2, 1.5, 1.5];
  //           let remaining = Math.max(0, Math.min(5, randomHalfMark));
  //           const stepBreakdown = caps.map((cap, idx) => {
  //             const ideal = remaining / (caps.length - idx);
  //             let awarded = Math.min(cap, ideal);
  //             awarded = roundToHalfGlobal(awarded);
  //             if (awarded > remaining)
  //               awarded = roundToHalfGlobal(Math.max(0, remaining));
  //             awarded = Math.min(cap, awarded);
  //             remaining = Math.max(0, roundToHalfGlobal(remaining - awarded));
  //             return {
  //               step: idx + 1,
  //               feedback: `Step ${
  //                 idx + 1
  //               } feedback: concise note on correctness and issues.`,
  //               marks: awarded,
  //               maxMarks: cap,
  //             };
  //           });
  //           return {
  //             id: q.id,
  //             questionImage: URL.createObjectURL(q.questionImage),
  //             answerImages: q.answerImages.map((img) =>
  //               URL.createObjectURL(img)
  //             ),
  //             marks: randomHalfMark,
  //             totalMarks: 5,
  //             feedback: `Question ${
  //               index + 1
  //             }: Good attempt with room for improvement.`,
  //             extractedContent: {
  //               problem:
  //                 "Detected problem statement from the question image (placeholder).",
  //             },
  //             stepBreakdown,
  //           };
  //         });
  //         const overall5 = roundToHalfGlobal(
  //           generatedQuestions.reduce((s, q) => s + q.marks, 0)
  //         );
  //         const total5 = generatedQuestions.length * 5;
  //         const newSheet = {
  //           id: Date.now(),
  //           fileName: `Answer_Sheet_${
  //             new Date().toISOString().split("T")[0]
  //           }.pdf`,
  //           thumbnail: getSubjectIcon("General"), // Use subject icon instead of uploaded image
  //           subject: "General", // Default subject for new uploads
  //           overallMarks: overall5,
  //           totalMarks: total5,
  //           date: new Date().toISOString().split("T")[0],
  //           questions: generatedQuestions,
  //         };
  //         setEvaluatedSheets((prev) => [newSheet, ...prev]);
  //         toast.success("Answer sheet evaluated successfully!");
  //         return 100;
  //       }
  //       return prev + 10;
  //     });
  //   }, 200);

  //   // Mock API call
  //   try {
  //     const formData = new FormData();
  //     uploadData.questions.forEach((q, index) => {
  //       formData.append(`question_${index}_image`, q.questionImage);
  //       formData.append(`question_${index}_marks`, q.marks);
  //       q.answerImages.forEach((img, imgIndex) => {
  //         formData.append(`question_${index}_answer_${imgIndex}`, img);
  //       });
  //     });

  //     // Simulate API call
  //     await new Promise((resolve) => setTimeout(resolve, 2000));
  //   } catch (error) {
  //     console.error("Error processing answer sheet:", error);
  //     toast.error("Failed to process answer sheet");
  //     setIsProcessing(false);
  //   }
  // };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="min-h-screen bg-gray-50 p-4">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Evaluate Answer Sheets
            </h1>
            <p className="text-gray-600">
              Upload and evaluate your answer sheets with AI-powered feedback
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={handleUploadNew}
              className="flex items-center gap-2 px-4 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#6B56E8] transition-colors"
            >
              <FaUpload className="w-4 h-4" />
              Upload New
            </button>
            {/* <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300"
                checked={isAlreadyMarked}
                onChange={(e) => setIsAlreadyMarked(e.target.checked)}
              />
              Answer Sheet is already marked
            </label> */}
          </div>
        </div>

        {/* Processing Progress Bar */}
        {isProcessing && (
          <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Processing Answer Sheet...
              </span>
              <span className="text-sm text-gray-500">
                {processingProgress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex gap-6">
          {/* Left Panel - Evaluated Sheets List */}
          <div className="w-1/3 bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">
                Evaluated Sheets
              </h2>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[70vh] pr-2">
              {isEvaluatedSheetsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <SimpleThreeDotsLoader message="Loading evaluated sheets" />
                </div>
              ) : evaluatedSheets.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500 px-2">
                  {evaluatedSheetsError ||
                    "No evaluated sheets available yet. Upload a sheet to see results here."}
                </div>
              ) : (
                evaluatedSheets.map((sheet) => {
                  const totalMarks = Number(sheet?.totalMarks ?? 0);
                  const overallMarks = Number(sheet?.overallMarks ?? 0);
                  const sanitizedOverall = Number.isFinite(overallMarks)
                    ? overallMarks
                    : 0;
                  const totalLabel = totalMarks > 0 ? totalMarks : "-";
                  const percentage =
                    totalMarks > 0
                      ? Math.round((sanitizedOverall / totalMarks) * 100)
                      : 0;

                  // Format date and time from date field in local timezone
                  const formatDateTime = (dateString) => {
                    if (!dateString) return "Date not available";
                    try {
                      // Parse the date string (API already includes timezone info)
                      const date = new Date(dateString);
                      const now = new Date();
                      const diffInHours = (now - date) / (1000 * 60 * 60);

                      if (diffInHours < 24) {
                        // Show time if within 24 hours (local timezone)
                        return date.toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                      } else if (diffInHours < 24 * 7) {
                        // Show date and day with time if within a week (local timezone)
                        const dateStr = date.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                        const dayTimeStr = date.toLocaleDateString(undefined, {
                          weekday: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                        return `${dateStr}, ${dayTimeStr}`;
                      } else {
                        // Show full date and time for older notes (local timezone)
                        return date.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                      }
                    } catch (error) {
                      console.error("Error formatting date:", error);
                      return "Date not available";
                    }
                  };

                  const formattedDate = formatDateTime(sheet?.date);

                  // Determine status based on sheet data
                  const getStatus = (sheet) => {
                    if (sheet?.status) {
                      return sheet.status.toLowerCase();
                    }
                    // If no explicit status, determine from completion
                    if (sheet?.questions && sheet.questions.length > 0) {
                      return "completed";
                    }
                    return "processing";
                  };

                  const status = getStatus(sheet);
                  const isCompleted = status === "completed";
                  const isProcessing = status === "processing";

                  // Check viewed status - default to true if not specified
                  const isViewed = sheet?.viewed !== false;
                  const isUnviewed = !isViewed;

                  return (
                    <div
                      key={sheet.id}
                      onClick={() => handleViewResults(sheet)}
                      className={`flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors ${
                        isUnviewed
                          ? "bg-green-50 border-l-4 border-green-400"
                          : ""
                      }`}
                    >
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-gray-200">
                        <div className="w-12 h-12 flex items-center justify-center">
                          <img
                            src={getSubjectIcon(sheet.subject)}
                            alt={`${sheet.subject ?? "Worksheet"} Icon`}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              // Prevent infinite error loops and use a local fallback icon
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = SUBJECT_DEFAULT_ICON;
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="space-y-1">
                          <p
                            className={`text-sm font-medium truncate ${
                              isUnviewed ? "text-green-800" : "text-gray-800"
                            }`}
                          >
                            {sheet.fileName || "Untitled worksheet"}
                          </p>
                          {sheet.subject && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-md">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                              <span
                                className="text-xs font-medium truncate"
                                style={{ color: "#0D99FF" }}
                              >
                                {sheet.subject}
                              </span>
                            </div>
                          )}
                          <p className="text-xs text-gray-500">
                            {formattedDate}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-green-600">
                              {sanitizedOverall}/{totalLabel}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({percentage}%)
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {isUnviewed && (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                                New
                              </span>
                            )}
                            {isCompleted && (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                                Completed
                              </span>
                            )}
                            {isProcessing && (
                              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">
                                Processing
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <FaEye
                        className={`w-4 h-4 ${
                          isUnviewed ? "text-green-500" : "text-gray-400"
                        }`}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Panel - Instructions */}
          <div className="flex-1 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              How to Use
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-700 font-semibold text-sm">
                  1
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">
                    Upload Your Answer Sheet
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Click "Upload New" to start evaluating a new answer sheet
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-700 font-semibold text-sm">
                  2
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">
                    Add Question and Answer Images
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Upload question images and corresponding answer images for
                    each question
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-700 font-semibold text-sm">
                  3
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">
                    Get AI Evaluation
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Our AI will analyze your answers and provide step wise
                    feedback with marks
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-700 font-semibold text-sm">
                  4
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Review Results</h3>
                  <p className="text-gray-600 text-sm">
                    Click on any evaluated sheet to view detailed feedback and
                    marks
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Modal - Full Screen */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
            <div className="bg-white w-full h-full flex flex-col overflow-hidden">
              {/* Header */}
              <div
                className="flex items-center justify-between px-6 py-2 border-b bg-[#0D99FF] text-white flex-shrink-0"
                style={{ height: "5%" }}
              >
                <h2 className="text-xl font-semibold text-white">
                  Verify Offline Work
                </h2>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setShowSidePanel(false);
                    setAskDoubtText("");
                  }}
                  className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              {/* Stepper for multi-step (shown for both modes) */}

              <div className="px-6 mt-2 flex justify-end flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setShowSidePanel(false);
                    setAskDoubtText("");
                  }}
                  className="text-[#0D99FF] font-semibold bg-transparent border-0 shadow-none focus:outline-none hover:text-[#5d4de2] flex items-center gap-2"
                >
                  <FileText size={16} />
                  Evaluated Sheets
                </button>
              </div>

              {/* Content */}
              <div
                className="flex-1 p-6 pt-0 overflow-y-auto"
                style={{ minHeight: 0 }}
              >
                {currentStep === 3 && (
                  <div
                    className="w-full flex justify-center mb-4 flex-shrink-0"
                    style={{ minHeight: "60px" }}
                  >
                    <div className="w-full flex items-center justify-center">
                      {(STEP2_DISABLED ? [1, 3] : [1, 2, 3]).map(
                        (step, idx, arr) => {
                          const isDone = completedSteps.has(step);
                          const isActive = currentStep === step;
                          const base = isDone
                            ? "bg-green-500 text-white"
                            : isActive
                            ? "bg-[#0D99FF] text-white"
                            : "bg-gray-200 text-gray-700";
                          return (
                            <div key={step} className="flex items-center">
                              <div
                                className={`flex items-center gap-2 px-3 py-2 rounded-full ${base}`}
                              >
                                {isDone ? (
                                  <FaCheckCircle className="w-4 h-4" />
                                ) : (
                                  <span className="w-4 h-4 rounded-full border-2 border-white" />
                                )}
                                <span className="text-sm font-medium">
                                  {step === 1
                                    ? "Upload"
                                    : step === 2
                                    ? "Review"
                                    : "Preview"}
                                </span>
                              </div>
                              {idx < arr.length - 1 && (
                                <div className="w-8 h-1 bg-gray-200 mx-4 rounded"></div>
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                <div className="w-[95%] mx-auto">
                  {/* Multi-step content (same for both modes) */}

                  <>
                    {currentStep === 1 && (
                      <div className="space-y-4">
                        {/* Upload Options Selection */}
                        <div className="bg-white  border-gray-300 ">
                          <div className="flex flex-col gap-4">
                            {/* <h3 className="text-lg font-semibold text-gray-800">
                              Choose Upload Method
                            </h3> */}
                            <div className="rounded-full w-full flex flex-row items-center justify-center mt-5">
                              <div className="bg-gray-100 w-min rounded-full ">
                                <div className="flex gap-2 md:gap-3 overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0 justify-center items-center rounded-full">
                                  {EVALUATE_UPLOAD_TABS.map((tab) => {
                                    const Icon = tab.icon;
                                    const isActive = activeUploadTab === tab.id;
                                    return (
                                      <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() =>
                                          handleUploadTabChange(tab.id)
                                        }
                                        className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex items-center gap-2 flex-shrink-0 ${
                                          isActive
                                            ? "bg-[#0D99FF] text-white shadow-md"
                                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        }`}
                                      >
                                        <Icon
                                          className={`w-4 h-4 ${
                                            isActive
                                              ? "text-white"
                                              : "text-gray-500"
                                          }`}
                                        />
                                        {tab.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Upload Content Based on Selected Option */}
                        {step1UploadOption === "separate" && (
                          <div className="space-y-4">
                            <div
                              style={{ marginBottom: 0 }}
                              className="flex flex-row w-full mb-0"
                            >
                              <div className="hidden lg:block w-1/2" />
                              <div className=" w-1/2 flex items-center justify-center">
                                <p
                                  style={{ marginBottom: "0px" }}
                                  className="text-[11px] text-[#0D99FF] text-center lg:text-left"
                                >
                                  Question & Answer in one file? Drop it here
                                </p>
                              </div>
                            </div>
                            <div
                              style={{ marginTop: "0px" }}
                              className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start"
                            >
                              <div className="bg-[#F9FAFF]">
                                <div className="flex flex-col">
                                  <input
                                    type="file"
                                    accept="image/*,application/pdf,.pdf"
                                    multiple
                                    capture="environment"
                                    onChange={handleStep1QuestionUpload}
                                    className="hidden"
                                    id="step1-questions"
                                  />
                                  <label
                                    htmlFor="step1-questions"
                                    className={`flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 text-center transition-colors duration-200 cursor-pointer min-h-[240px] hover:border-[#0D99FF] hover:bg-[#0D99FF]/5 ${
                                      dropZoneHover.question
                                        ? "border-[#0D99FF] bg-[#0D99FF]/10 shadow-inner"
                                        : "border-gray-200 bg-white"
                                    }`}
                                    onDragEnter={(e) =>
                                      handleDropZoneDragEnter(e, "question")
                                    }
                                    onDragLeave={(e) =>
                                      handleDropZoneDragLeave(e, "question")
                                    }
                                    onDragOver={handleDropZoneDragOver}
                                    onDrop={(e) =>
                                      handleDropZoneDrop(e, "question")
                                    }
                                  >
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0D99FF]/10 text-[#0D99FF]">
                                      <FaQuestionCircle className="h-6 w-6" />
                                    </div>
                                    <p className="text-lg font-semibold text-gray-900">
                                      The Questions
                                    </p>
                                    <p className="text-sm text-gray-500 max-w-xs">
                                      Upload or drag & drop the question paper
                                      here.
                                    </p>
                                    <span className="text-xs font-semibold uppercase tracking-wide text-[#0D99FF]">
                                      Add from Gallery / Files / Camera
                                    </span>
                                  </label>
                                  {step1QuestionImages.length > 0 && (
                                    <div className="mt-2">
                                      <p className="mb-1.5 text-xs font-medium text-gray-600">
                                        Uploaded files
                                      </p>
                                      <div className="grid grid-cols-4 gap-1.5">
                                        {step1QuestionImages.map((f, idx) => {
                                          const isPdf =
                                            (f.type &&
                                              f.type.includes("pdf")) ||
                                            (f.name &&
                                              f.name
                                                .toLowerCase()
                                                .endsWith(".pdf"));
                                          const allItems =
                                            step1QuestionImages.map((file) => {
                                              const ipdf =
                                                (file.type &&
                                                  file.type.includes("pdf")) ||
                                                (file.name &&
                                                  file.name
                                                    .toLowerCase()
                                                    .endsWith(".pdf"));
                                              return {
                                                src: URL.createObjectURL(file),
                                                isPdf: !!ipdf,
                                              };
                                            });
                                          const myItem = {
                                            src: URL.createObjectURL(f),
                                            isPdf,
                                          };
                                          const myIndex = allItems.findIndex(
                                            (it) => it.src === myItem.src
                                          );
                                          return (
                                            <div
                                              key={idx}
                                              className="relative group"
                                            >
                                              <div className="w-full h-16 border rounded flex items-center justify-center bg-gray-50 text-xs text-gray-700 p-0 overflow-hidden">
                                                {isPdf ? (
                                                  <button
                                                    onClick={() =>
                                                      handleImagePreview(
                                                        myItem.src,
                                                        f.name || "PDF",
                                                        allItems,
                                                        Math.max(0, myIndex)
                                                      )
                                                    }
                                                    className="w-full h-full flex items-center justify-center hover:bg-gray-100 relative"
                                                  >
                                                    {step1QuestionThumbnails.has(
                                                      f
                                                    ) ? (
                                                      <img
                                                        src={step1QuestionThumbnails.get(
                                                          f
                                                        )}
                                                        alt={`PDF thumbnail: ${
                                                          f.name ||
                                                          "document.pdf"
                                                        }`}
                                                        className="w-full h-full object-cover"
                                                      />
                                                    ) : (
                                                      <span className="px-2 text-center">
                                                        PDF:{" "}
                                                        {f.name ||
                                                          "document.pdf"}{" "}
                                                        (tap to preview)
                                                      </span>
                                                    )}
                                                    <div className="absolute top-0.5 left-0.5 bg-red-600 text-white text-[10px] px-0.5 py-0 rounded">
                                                      PDF
                                                    </div>
                                                  </button>
                                                ) : (
                                                  <img
                                                    src={myItem.src}
                                                    alt={`Question ${idx + 1}`}
                                                    className="w-full h-full object-cover cursor-zoom-in"
                                                    onClick={() =>
                                                      handleImagePreview(
                                                        myItem.src,
                                                        `Question Paper`,
                                                        allItems,
                                                        Math.max(0, myIndex)
                                                      )
                                                    }
                                                  />
                                                )}
                                              </div>
                                              <button
                                                onClick={() =>
                                                  removeStep1QuestionImage(idx)
                                                }
                                                className="absolute top-0.5 right-0.5 p-0.5 hover:bg-gray-100 rounded transition-colors bg-white/80"
                                                title="Remove this file"
                                              >
                                                <FaTimes className="w-3 h-3 text-[#0D99FF] hover:text-[#6B56E8]" />
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div
                                className="bg-[#F9FAFF] self-start"
                                style={{ minHeight: "240px" }}
                              >
                                <div className="flex flex-col">
                                  <input
                                    type="file"
                                    accept="image/*,application/pdf,.pdf"
                                    multiple
                                    capture="environment"
                                    onChange={handleStep1AnswerUpload}
                                    className="hidden"
                                    id="step1-answers"
                                  />

                                  <label
                                    htmlFor="step1-answers"
                                    className={`flex flex-1 flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed text-center transition-colors duration-200 cursor-pointer min-h-[240px] hover:border-[#0D99FF] hover:bg-[#0D99FF]/5 ${
                                      dropZoneHover.answer
                                        ? "border-[#0D99FF] bg-[#0D99FF]/10 shadow-inner"
                                        : "border-[#CFC9FD] bg-white"
                                    }`}
                                    onDragEnter={(e) =>
                                      handleDropZoneDragEnter(e, "answer")
                                    }
                                    onDragLeave={(e) =>
                                      handleDropZoneDragLeave(e, "answer")
                                    }
                                    onDragOver={handleDropZoneDragOver}
                                    onDrop={(e) =>
                                      handleDropZoneDrop(e, "answer")
                                    }
                                  >
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0D99FF]/10 text-[#0D99FF]">
                                      <FaCheckCircle className="h-6 w-6" />
                                    </div>
                                    <p className="text-lg font-semibold text-gray-900">
                                      Your Answers
                                    </p>
                                    <p className="text-sm text-gray-500 max-w-xs">
                                      Upload or drag & drop your answer sheet
                                      here.
                                    </p>
                                    <span className="text-xs font-semibold uppercase tracking-wide text-[#0D99FF]">
                                      Add from Gallery / Files / Camera
                                    </span>
                                  </label>
                                  {step1AnswerImages.length > 0 && (
                                    <div className="mt-2">
                                      <p className="mb-1.5 text-xs font-medium text-gray-600">
                                        Uploaded files
                                      </p>
                                      <div className="grid grid-cols-4 gap-1.5">
                                        {step1AnswerImages.map((f, idx) => {
                                          const isPdf =
                                            (f.type &&
                                              f.type.includes("pdf")) ||
                                            (f.name &&
                                              f.name
                                                .toLowerCase()
                                                .endsWith(".pdf"));
                                          const allItems =
                                            step1AnswerImages.map((file) => {
                                              const itemIsPdf =
                                                (file.type &&
                                                  file.type.includes("pdf")) ||
                                                (file.name &&
                                                  file.name
                                                    .toLowerCase()
                                                    .endsWith(".pdf"));
                                              return {
                                                src: URL.createObjectURL(file),
                                                isPdf: !!itemIsPdf,
                                              };
                                            });
                                          const myItem = {
                                            src: URL.createObjectURL(f),
                                            isPdf,
                                          };
                                          const myIndex = allItems.findIndex(
                                            (it) => it.src === myItem.src
                                          );
                                          return (
                                            <div
                                              key={idx}
                                              className="relative group"
                                            >
                                              <div className="w-full h-16 border rounded flex items-center justify-center bg-gray-50 text-xs text-gray-700 p-0 overflow-hidden">
                                                {isPdf ? (
                                                  <button
                                                    onClick={() =>
                                                      handleImagePreview(
                                                        myItem.src,
                                                        `Answer Sheet`,
                                                        allItems,
                                                        Math.max(0, myIndex)
                                                      )
                                                    }
                                                    className="w-full h-full flex items-center justify-center hover:bg-gray-100 relative"
                                                  >
                                                    {step1AnswerThumbnails.has(
                                                      f
                                                    ) ? (
                                                      <img
                                                        src={step1AnswerThumbnails.get(
                                                          f
                                                        )}
                                                        alt={`PDF thumbnail: ${
                                                          f.name ||
                                                          "document.pdf"
                                                        }`}
                                                        className="w-full h-full object-cover"
                                                      />
                                                    ) : (
                                                      <span className="px-2 text-center">
                                                        PDF:{" "}
                                                        {f.name ||
                                                          "document.pdf"}{" "}
                                                        (tap to preview)
                                                      </span>
                                                    )}
                                                    <div className="absolute top-0.5 left-0.5 bg-red-600 text-white text-[10px] px-0.5 py-0 rounded">
                                                      PDF
                                                    </div>
                                                  </button>
                                                ) : (
                                                  <img
                                                    src={myItem.src}
                                                    alt={`Answer ${idx + 1}`}
                                                    className="w-full h-full object-cover cursor-zoom-in"
                                                    onClick={() =>
                                                      handleImagePreview(
                                                        myItem.src,
                                                        `Answer Sheet`,
                                                        allItems,
                                                        Math.max(0, myIndex)
                                                      )
                                                    }
                                                  />
                                                )}
                                              </div>
                                              <button
                                                onClick={() =>
                                                  removeStep1AnswerImage(idx)
                                                }
                                                className="absolute top-0.5 right-0.5 p-0.5 hover:bg-gray-100 rounded transition-colors bg-white/80"
                                                title="Remove this file"
                                              >
                                                <FaTimes className="w-3 h-3 text-[#0D99FF] hover:text-[#6B56E8]" />
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {step1UploadOption === "combined" && (
                          <div className="">
                            <div className="flex flex-col">
                              <input
                                type="file"
                                accept="image/*,application/pdf,.pdf"
                                multiple
                                capture="environment"
                                onChange={handleStep1CombinedUpload}
                                className="hidden"
                                id="step1-combined"
                              />
                              {step1CombinedImages.length > 0 && (
                                <div className="mt-4">
                                  <p className="mb-2 text-sm font-medium text-gray-600">
                                    Uploaded files
                                  </p>
                                  <div className="grid grid-cols-3 gap-2">
                                    {step1CombinedImages.map((f, idx) => {
                                      const isPdf =
                                        (f.type && f.type.includes("pdf")) ||
                                        (f.name &&
                                          f.name
                                            .toLowerCase()
                                            .endsWith(".pdf"));
                                      const allItems = step1CombinedImages.map(
                                        (file) => {
                                          const ipdf =
                                            (file.type &&
                                              file.type.includes("pdf")) ||
                                            (file.name &&
                                              file.name
                                                .toLowerCase()
                                                .endsWith(".pdf"));
                                          return {
                                            src: URL.createObjectURL(file),
                                            isPdf: !!ipdf,
                                          };
                                        }
                                      );
                                      const myItem = {
                                        src: URL.createObjectURL(f),
                                        isPdf,
                                      };
                                      const myIndex = allItems.findIndex(
                                        (it) => it.src === myItem.src
                                      );
                                      return (
                                        <div
                                          key={idx}
                                          className="relative group"
                                        >
                                          <div className="w-full h-28 border rounded flex items-center justify-center bg-gray-50 text-xs text-gray-700 p-0 overflow-hidden">
                                            {isPdf ? (
                                              <button
                                                onClick={() =>
                                                  handleImagePreview(
                                                    myItem.src,
                                                    f.name || "PDF",
                                                    allItems,
                                                    Math.max(0, myIndex)
                                                  )
                                                }
                                                className="w-full h-full flex items-center justify-center hover:bg-gray-100 relative"
                                              >
                                                {step1CombinedThumbnails.has(
                                                  f
                                                ) ? (
                                                  <img
                                                    src={step1CombinedThumbnails.get(
                                                      f
                                                    )}
                                                    alt={`PDF thumbnail: ${
                                                      f.name || "document.pdf"
                                                    }`}
                                                    className="w-full h-full object-cover"
                                                  />
                                                ) : (
                                                  <span className="px-2 text-center">
                                                    PDF:{" "}
                                                    {f.name || "document.pdf"}{" "}
                                                    (tap to preview)
                                                  </span>
                                                )}
                                                <div className="absolute top-1 left-1 bg-red-600 text-white text-xs px-1 py-0.5 rounded">
                                                  PDF
                                                </div>
                                              </button>
                                            ) : (
                                              <img
                                                src={myItem.src}
                                                alt={`Combined ${idx + 1}`}
                                                className="w-full h-full object-cover cursor-zoom-in"
                                                onClick={() =>
                                                  handleImagePreview(
                                                    myItem.src,
                                                    `Combined ${idx + 1}`,
                                                    allItems,
                                                    Math.max(0, myIndex)
                                                  )
                                                }
                                              />
                                            )}
                                          </div>
                                          <button
                                            onClick={() =>
                                              removeStep1CombinedImage(idx)
                                            }
                                            className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded transition-colors"
                                            title="Remove this file"
                                          >
                                            <FaTimes className="w-4 h-4 text-[#0D99FF] hover:text-[#6B56E8]" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <label
                                htmlFor="step1-combined"
                                className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 text-center transition-colors duration-200 cursor-pointer min-h-[240px] hover:border-[#4C83FF] hover:bg-[#4C83FF]/5 ${
                                  dropZoneHover.combined
                                    ? "border-[#4C83FF] bg-[#4C83FF]/10 shadow-inner"
                                    : "border-[#9EC5FF] bg-white"
                                }`}
                                onDragEnter={(e) =>
                                  handleDropZoneDragEnter(e, "combined")
                                }
                                onDragLeave={(e) =>
                                  handleDropZoneDragLeave(e, "combined")
                                }
                                onDragOver={handleDropZoneDragOver}
                                onDrop={(e) =>
                                  handleDropZoneDrop(e, "combined")
                                }
                              >
                                <p className="text-base font-semibold text-gray-900">
                                  Upload or drag & drop the combined question &
                                  answer PDF/images here.
                                </p>
                                <span className="text-xs font-semibold uppercase tracking-wide text-[#1C56F1]">
                                  Add from Gallery / Files / Camera
                                </span>
                              </label>
                            </div>
                          </div>
                        )}

                        {step1UploadOption === "worksheet" && (
                          <>
                            {/* Worksheet upload UI temporarily disabled for Anya Practice */}
                            {false && (
                              <div className="grid grid-cols-1">
                                {/* Worksheet Selection - Left Side */}
                                <div className=" px-6 bg-white">
                                  <div className="mb-4 flex items-center justify-between gap-3">
                                    <h3 className="text-md font-medium text-gray-800">
                                      Select Worksheet from List
                                    </h3>
                                    <button
                                      type="button"
                                      className="text-sm font-medium text-[#0D99FF] hover:text-[#5d49cc] transition-colors"
                                    >
                                      View All
                                    </button>
                                  </div>
                                  <div className="">
                                    <div className="flex gap-3 overflow-x-auto pb-2 flex-nowrap">
                                      {availableWorksheets.map(
                                        (worksheet, index) => {
                                          if (index >= 3) return null; // Limit to first 4 worksheets
                                          const isSelected =
                                            selectedWorksheet?.id ===
                                            worksheet.id;
                                          const createdLabel =
                                            worksheet.createdAt
                                              ? new Date(
                                                  worksheet.createdAt
                                                ).toLocaleDateString(
                                                  undefined,
                                                  {
                                                    month: "short",
                                                    day: "numeric",
                                                  }
                                                )
                                              : null;
                                          return (
                                            <button
                                              type="button"
                                              key={worksheet.id}
                                              onClick={() =>
                                                handleWorksheetSelection(
                                                  worksheet
                                                )
                                              }
                                              className={`w-full min-w-[220px] max-w-[260px] flex-shrink-0 rounded-xl border-2 bg-white p-4 text-left transition-all ${
                                                isSelected
                                                  ? "border-[#0D99FF] bg-[#0D99FF]/5 shadow-sm"
                                                  : "border-gray-200 hover:border-[#0D99FF]/60"
                                              }`}
                                            >
                                              <p
                                                style={{ marginBottom: "0px" }}
                                                className="text-base font-semibold text-gray-900"
                                              >
                                                {worksheet.title}
                                              </p>

                                              <p
                                                style={{ marginBottom: "0px" }}
                                                className="text-xs text-gray-400"
                                              >
                                                {/* {worksheet.questionCount} questions
                                              {worksheet.difficulty
                                                ? ` • ${worksheet.difficulty}`
                                                : ""} */}
                                                {createdLabel
                                                  ? ` • Generated ${createdLabel}`
                                                  : ""}
                                              </p>
                                              <span className="mt-3 inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                                                Pending
                                              </span>
                                            </button>
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Answer Sheet Upload - Right Side */}
                                <div className="px-6 mt-1 bg-white">
                                  <div className="flex flex-col">
                                    <input
                                      type="file"
                                      accept="image/*,application/pdf,.pdf"
                                      multiple
                                      capture="environment"
                                      onChange={handleStep1AnswerUpload}
                                      className="hidden"
                                      id="step1-worksheet-answers"
                                    />
                                    <label
                                      htmlFor="step1-worksheet-answers"
                                      className={`flex flex-1 flex-col items-center justify-center rounded-2xl border-2 border-dashed text-center transition-colors duration-200 py-5 cursor-pointer hover:border-[#0D99FF] hover:bg-[#0D99FF]/5 ${
                                        dropZoneHover.worksheetAnswer
                                          ? "border-[#0D99FF] bg-[#0D99FF]/10 shadow-inner"
                                          : "border-[#CFC9FD] bg-white"
                                      }`}
                                      onDragEnter={(e) =>
                                        handleDropZoneDragEnter(
                                          e,
                                          "worksheetAnswer"
                                        )
                                      }
                                      onDragLeave={(e) =>
                                        handleDropZoneDragLeave(
                                          e,
                                          "worksheetAnswer"
                                        )
                                      }
                                      onDragOver={handleDropZoneDragOver}
                                      onDrop={(e) =>
                                        handleDropZoneDrop(e, "worksheetAnswer")
                                      }
                                    >
                                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0D99FF]/10 text-[#0D99FF]">
                                        <FaCheckCircle className="h-6 w-6" />
                                      </div>
                                      <p
                                        style={{ marginBottom: "0px" }}
                                        className="text-lg font-semibold text-gray-900"
                                      >
                                        Drop your answer sheet here.
                                      </p>
                                      <p className="text-sm text-gray-500 max-w-xs">
                                        We already know the questions, just
                                        upload your solutions.
                                      </p>
                                    </label>
                                    {step1AnswerImages.length > 0 && (
                                      <div className="mt-6">
                                        <p className="mb-2 text-sm font-medium text-gray-600">
                                          Uploaded files
                                        </p>
                                        <div className="grid grid-cols-3 gap-2">
                                          {step1AnswerImages.map((f, idx) => {
                                            const isPdf =
                                              (f.type &&
                                                f.type.includes("pdf")) ||
                                              (f.name &&
                                                f.name
                                                  .toLowerCase()
                                                  .endsWith(".pdf"));
                                            const allItems =
                                              step1AnswerImages.map((file) => {
                                                const itemIsPdf =
                                                  (file.type &&
                                                    file.type.includes(
                                                      "pdf"
                                                    )) ||
                                                  (file.name &&
                                                    file.name
                                                      .toLowerCase()
                                                      .endsWith(".pdf"));
                                                return {
                                                  src: URL.createObjectURL(
                                                    file
                                                  ),
                                                  isPdf: !!itemIsPdf,
                                                };
                                              });
                                            const myItem = {
                                              src: URL.createObjectURL(f),
                                              isPdf,
                                            };
                                            const myIndex = allItems.findIndex(
                                              (it) => it.src === myItem.src
                                            );
                                            return (
                                              <div
                                                key={idx}
                                                className="relative group"
                                              >
                                                <div className="w-full h-16 border rounded flex items-center justify-center bg-gray-50 text-xs text-gray-700 p-0 overflow-hidden">
                                                  {isPdf ? (
                                                    <button
                                                      onClick={() =>
                                                        handleImagePreview(
                                                          myItem.src,
                                                          `Answer Sheet`,
                                                          allItems,
                                                          Math.max(0, myIndex)
                                                        )
                                                      }
                                                      className="w-full h-full flex items-center justify-center hover:bg-gray-100 relative"
                                                    >
                                                      {step1AnswerThumbnails.has(
                                                        f
                                                      ) ? (
                                                        <img
                                                          src={step1AnswerThumbnails.get(
                                                            f
                                                          )}
                                                          alt={`PDF thumbnail: ${
                                                            f.name ||
                                                            "document.pdf"
                                                          }`}
                                                          className="w-full h-full object-cover"
                                                        />
                                                      ) : (
                                                        <span className="px-2 text-center">
                                                          PDF:{" "}
                                                          {f.name ||
                                                            "document.pdf"}{" "}
                                                          (tap to preview)
                                                        </span>
                                                      )}
                                                      <div className="absolute top-1 left-1 bg-red-600 text-white text-xs px-1 py-0.5 rounded">
                                                        PDF
                                                      </div>
                                                    </button>
                                                  ) : (
                                                    <img
                                                      src={myItem.src}
                                                      alt={`Answer ${idx + 1}`}
                                                      className="w-full h-full object-cover cursor-zoom-in"
                                                      onClick={() =>
                                                        handleImagePreview(
                                                          myItem.src,
                                                          `Answer Sheet`,
                                                          allItems,
                                                          Math.max(0, myIndex)
                                                        )
                                                      }
                                                    />
                                                  )}
                                                </div>
                                                <button
                                                  onClick={() =>
                                                    removeStep1AnswerImage(idx)
                                                  }
                                                  className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded transition-colors"
                                                  title="Remove this file"
                                                >
                                                  <FaTimes className="w-4 h-4 text-[#0D99FF] hover:text-[#6B56E8]" />
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-white px-6 py-20 text-center">
                              <div className="flex items-center gap-3">
                                <FaLock className="text-3xl md:text-4xl text-[#0D99FF]" />
                                <p className="text-3xl md:text-4xl font-semibold text-[#0D99FF]">
                                  Coming soon
                                </p>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Question and Answer in Same Sheet Checkbox - Only show when upload option is selected */}

                        {/* <div className="flex flex-row justify-between align-center bg-white rounded-lg border border-gray-200 p-4 pb-0">
                          {(step1UploadOption === "upload" ||
                            step1UploadOption === "separate" ||
                            step1UploadOption === "combined") &&
                            step1UploadOption !== "worksheet" && (
                              <div className="w-1/2 flex flex-col justify-start align-top text-top pl-5">
                                <label className="flex items-center space-x-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={step1UploadOption === "combined"}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setStep1UploadOption("combined");
                                      } else {
                                        setStep1UploadOption("separate");
                                      }
                                    }}
                                    className="w-4 h-4 text-[#0D99FF] border-gray-300 rounded focus:ring-[#0D99FF]"
                                  />
                                  <span className="text-sm font-medium text-gray-700">
                                    Question and answer in same sheet
                                  </span>
                                </label>
                                <p className="text-xs text-gray-500 mt-1 ml-7">
                                  Check this if your question paper and answer
                                  sheet are in the same file
                                </p>
                              </div>
                            )}
                          <div className="w-1/2 flex justify-start align-top text-top pb-4 pl-5">
                            <label className="flex items-top space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isAnswerSheetMarked}
                                onChange={(e) =>
                                  setIsAnswerSheetMarked(e.target.checked)
                                }
                                className="w-4 h-4 text-[#0D99FF] border-gray-300 rounded focus:ring-[#0D99FF]"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Answer sheet is already marked
                              </span>
                            </label>
                          </div>
                        </div> */}
                      </div>
                    )}

                    {!STEP2_DISABLED && currentStep === 2 && (
                      <>
                        {isStep2Loading ? (
                          <EvaluateAnswerSheetLoader message="Generating evaluation results..." />
                        ) : step2Questions.length > 0 ? (
                          <>
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-gray-800">
                                Verify Questions
                              </h3>
                              {/* <button
                              onClick={verifyAllQuestions}
                              className="px-3 py-2 text-sm bg-[#0D99FF] text-white rounded hover:bg-[#6B56E8]"
                            >
                              Verify All Questions
                            </button> */}
                            </div>
                            <div className="space-y-6">
                              {step2Questions.map((question, index) => (
                                <div
                                  key={question.id}
                                  className="border-2 border-[#0D99FF]/20 rounded-lg bg-white"
                                >
                                  {/* Collapsible Header */}
                                  <div
                                    className="flex items-center justify-between p-4 border-b border-[#0D99FF]/20 cursor-pointer hover:bg-[#0D99FF]/5 rounded-t-lg"
                                    onClick={() =>
                                      toggleStep2Question(question.id)
                                    }
                                  >
                                    <div className="flex items-center gap-2">
                                      {step2ExpandedQuestions.has(
                                        question.id
                                      ) ? (
                                        <FaChevronDown className="w-4 h-4 text-[#0D99FF]" />
                                      ) : (
                                        <FaChevronRight className="w-4 h-4 text-[#0D99FF]" />
                                      )}
                                      <h4 className="text-lg font-medium text-[#0D99FF]">
                                        Question {index + 1}
                                      </h4>
                                      {verifiedQuestions.has(question.id) && (
                                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                          Verified
                                        </span>
                                      )}
                                    </div>
                                    <div
                                      className="flex items-center gap-3"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700">
                                          Total Marks:
                                        </label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          value={question.totalMarks || ""}
                                          onChange={(e) => {
                                            const value =
                                              parseFloat(e.target.value) || 0;
                                            setStep2Questions((prev) =>
                                              prev.map((q) =>
                                                q.id === question.id
                                                  ? { ...q, totalMarks: value }
                                                  : q
                                              )
                                            );
                                          }}
                                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0D99FF] focus:border-[#0D99FF]"
                                          placeholder="0"
                                        />
                                      </div>
                                      <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                                        <input
                                          type="checkbox"
                                          className="w-4 h-4"
                                          checked={verifiedQuestions.has(
                                            question.id
                                          )}
                                          onChange={() =>
                                            toggleVerifyQuestion(question.id)
                                          }
                                        />
                                        Verified
                                      </label>
                                      {step2Questions.length > 1 && (
                                        <button
                                          onClick={() =>
                                            removeQuestion(question.id)
                                          }
                                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                                          title="Remove this question"
                                        >
                                          <FaTimes className="w-4 h-4 text-[#0D99FF] hover:text-[#6B56E8]" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {/* Collapsible Content */}
                                  {step2ExpandedQuestions.has(question.id) && (
                                    <div className="p-6">
                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div>
                                          <h5 className="text-md font-medium text-gray-700 mb-3">
                                            Question Image
                                          </h5>
                                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center min-h-[200px] flex flex-col justify-center">
                                            <img
                                              src={
                                                question.questionImage ||
                                                "/images/question_example.png"
                                              }
                                              alt={`Question ${index + 1}`}
                                              className="max-w-full max-h-48 mx-auto rounded-lg cursor-zoom-in"
                                              onClick={() =>
                                                handleImagePreview(
                                                  question.questionImage ||
                                                    "/images/question_example.png",
                                                  `Question ${index + 1}`,
                                                  [
                                                    question.questionImage ||
                                                      "/images/question_example.png",
                                                  ],
                                                  0
                                                )
                                              }
                                            />
                                            <p className="text-sm text-gray-600">
                                              Placeholder question image
                                            </p>
                                          </div>
                                        </div>
                                        <div>
                                          <h5 className="text-md font-medium text-gray-700 mb-3">
                                            Answer Images
                                          </h5>
                                          <div className="space-y-3">
                                            {question.answerImages.map(
                                              (image, imgIndex) => (
                                                <div
                                                  key={imgIndex}
                                                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50"
                                                >
                                                  <img
                                                    src={
                                                      typeof image === "string"
                                                        ? image
                                                        : URL.createObjectURL(
                                                            image
                                                          )
                                                    }
                                                    alt={`Answer ${
                                                      imgIndex + 1
                                                    }`}
                                                    className="w-12 h-12 object-cover rounded border cursor-zoom-in"
                                                    onClick={() =>
                                                      handleImagePreview(
                                                        typeof image ===
                                                          "string"
                                                          ? image
                                                          : URL.createObjectURL(
                                                              image
                                                            ),
                                                        `Answer ${
                                                          imgIndex + 1
                                                        }`,
                                                        [
                                                          typeof image ===
                                                          "string"
                                                            ? image
                                                            : URL.createObjectURL(
                                                                image
                                                              ),
                                                        ],
                                                        0
                                                      )
                                                    }
                                                  />
                                                  <span className="text-sm text-gray-700 flex-1 truncate">
                                                    {typeof image === "string"
                                                      ? "Image"
                                                      : image.name}
                                                  </span>
                                                  <button
                                                    onClick={() =>
                                                      removeAnswerImage(
                                                        question.id,
                                                        imgIndex
                                                      )
                                                    }
                                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                    title="Remove this answer image"
                                                  >
                                                    <FaTimes className="w-4 h-4 text-[#0D99FF] hover:text-[#6B56E8]" />
                                                  </button>
                                                </div>
                                              )
                                            )}
                                            <input
                                              type="file"
                                              accept="image/*"
                                              multiple
                                              capture="environment"
                                              onChange={(e) =>
                                                handleAnswerImageUpload(
                                                  question.id,
                                                  e
                                                )
                                              }
                                              className="hidden"
                                              id={`answer-${question.id}`}
                                            />
                                            <label
                                              htmlFor={`answer-${question.id}`}
                                              className="block w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-[#0D99FF] hover:bg-[#0D99FF]/5 transition-colors min-h-[120px] flex flex-col justify-center"
                                            >
                                              <FaUpload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                                              <span className="text-sm text-gray-600">
                                                Add Answer Images (Gallery /
                                                Camera)
                                              </span>
                                            </label>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Hide Add Another Question in Step 2 */}
                          </>
                        ) : null}
                      </>
                    )}

                    {currentStep === 3 && (
                      <>
                        {isStep3Loading || isSSEProcessing ? (
                          <div className="space-y-4 h-full overflow-y-auto">
                            <EvaluateAnswerSheetLoader
                              message={
                                sseStatus === "submitting"
                                  ? "Submitting worksheet for evaluation..."
                                  : sseStatus === "processing"
                                  ? "Processing evaluation with AI..."
                                  : "Generating evaluation results..."
                              }
                            />
                            <div className="flex flex-col items-center space-y-4">
                              <div
                                className="text-lg text-gray-700 font-medium text-center"
                                aria-live="polite"
                              >
                                "Please wait on this page to see the response or
                                you can close this and find it in the worksheet
                                list."
                              </div>
                              <button
                                onClick={() => {
                                  // Reset all state variables and loaders
                                  setShowUploadModal(false);
                                  setCurrentStep(1);
                                  setCompletedSteps(new Set());
                                  setStep1QuestionImages([]);
                                  setStep1AnswerImages([]);
                                  setStep1QuestionThumbnails(new Map());
                                  setIsUploading(false);
                                  setShowConfirmationModal(false);
                                  setStep1ApiResponse(null);
                                  setIsStep3Loading(false);
                                  setIsStep2Loading(false);
                                  setVerifiedQuestions(new Set());
                                  setPreviewSheet(null);
                                  setStep2ExpandedQuestions(new Set([1]));
                                  setStep3ExpandedQuestions(new Set([1]));
                                  setStep2Questions([]);
                                  setStep3Data(null);
                                  setShowImagePreview(false);
                                  setPreviewImage(null);
                                  setPreviewImageAlt("");
                                  setPreviewImages([]);
                                  setPreviewIndex(0);
                                  setExpandedQuestions(new Set([1]));
                                  setUploadData({
                                    questions: [
                                      {
                                        id: 1,
                                        questionImage: null,
                                        answerImages: [],
                                        marks: 0,
                                      },
                                    ],
                                  });
                                  step2SeededRef.current = false;

                                  // Reset SSE state
                                  setIsSSEProcessing(false);
                                  setSseProgress(0);
                                  setSseStatus("idle");
                                  setSseError(null);
                                  setCurrentRequestId(null);
                                  if (sseSubscriptionRef.current) {
                                    try {
                                      sseSubscriptionRef.current();
                                    } catch {
                                      // ignore
                                    }
                                    sseSubscriptionRef.current = null;
                                  }
                                }}
                                className="px-6 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#6B56E8] transition-colors"
                              >
                                Close
                              </button>
                            </div>

                            {/* {isSSEProcessing && (
                              <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-700">
                                    Evaluation Progress
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {sseProgress}%
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-[#0D99FF] h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${sseProgress}%` }}
                                  ></div>
                                </div>
                                {sseError && (
                                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600">
                                      {sseError}
                                    </p>
                                    <div className="mt-2 flex gap-2">
                                      <button
                                        onClick={() =>
                                          getEvaluationStatus(currentRequestId)
                                        }
                                        className="text-xs text-red-600 hover:text-red-800 underline"
                                      >
                                        Check status manually
                                      </button>
                                      <button
                                        onClick={cancelEvaluation}
                                        className="text-xs text-red-600 hover:text-red-800 underline"
                                      >
                                        Cancel evaluation
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {!sseError && isSSEProcessing && (
                                  <div className="mt-3 flex justify-center">
                                    <button
                                      onClick={cancelEvaluation}
                                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                      Cancel Evaluation
                                    </button>
                                  </div>
                                )}
                              </div>
                            )} */}
                          </div>
                        ) : step3Data ? (
                          <div
                            className={`${
                              isMobile
                                ? "flex flex-col"
                                : "flex w-full items-stretch"
                            } h-full max-h-full overflow-hidden`}
                            ref={!isMobile ? resizeRef : undefined}
                          >
                            {/*!shouldShowRightUtilityPanel */}
                            {!shouldShowRightUtilityPanel  && (
                              <>
                                {renderLeftUtilityPanel(leftInfoPanelWidth)}
                                <div
                                  className="w-4 h-auto flex flex-col justify-center items-center cursor-col-resize"
                                  onMouseDown={handleLeftResizerMouseDown}
                                  title="Drag to resize"
                                >
                                  <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                                  <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                                    <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                    </div>
                                  </div>
                                  <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                                </div>
                              </>
                            )}
                            <div
                              className="flex flex-col space-y-4 overflow-y-auto"
                              style={{
                                width: centerPanelWidth,
                                height: "100%",
                                maxHeight: "100%",
                              }}
                            >
                              <div className="flex items-center justify-between px-4 py-1 border-2 border-[#0D99FF]/20 rounded-lg bg-gradient-to-r from-[#0D99FF]/5 to-[#0D99FF]/10 shadow-sm">
                                <div className="flex-1">
                                  <h2 className="text-lg font-bold text-gray-800 mb-0.5">
                                    {step3Data.fileName}
                                  </h2>
                                  <p className="text-xs text-gray-600">
                                    Test Evaluation
                                  </p>
                                </div>
                                <div className="text-center">
                                  <div>
                                    <p
                                      style={{ marginBottom: "0px" }}
                                      className="text-xs font-medium text-gray-600 mb-0"
                                    >
                                      Overall Score
                                    </p>
                                    <div className="flex items-baseline justify-center gap-0.5">
                                      <span className="text-lg font-bold text-[#0D99FF]">
                                        {step3Data.overallMarks}
                                      </span>
                                      <span className="text-sm text-gray-500">
                                        /
                                      </span>
                                      <span className="text-base font-semibold text-gray-700">
                                        {step3Data.totalMarks}
                                      </span>
                                    </div>
                                    <div className="rounded-full text-center">
                                      <span className="text-sm font-semibold text-[#0D99FF]">
                                        {"   ( " +
                                          Math.round(
                                            (step3Data.overallMarks /
                                              step3Data.totalMarks) *
                                              100
                                          ) +
                                          "% )"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-4">
                                {step3Data.questions.map((question, index) => (
                                  <div
                                    key={question.id}
                                    className="border-2 border-[#0D99FF]/20 rounded-lg shadow-sm bg-white"
                                  >
                                    {/* Collapsible Header */}
                                    <div
                                      className="flex items-center justify-between p-3 bg-white border-b border-[#0D99FF]/20 rounded-t-lg cursor-pointer hover:bg-[#0D99FF]/5"
                                      onClick={() =>
                                        toggleStep3Question(question.id)
                                      }
                                    >
                                      <div className="flex items-center gap-2">
                                        {step3ExpandedQuestions.has(
                                          question.id
                                        ) ? (
                                          <FaChevronDown className="w-4 h-4 text-[#0D99FF]" />
                                        ) : (
                                          <FaChevronRight className="w-4 h-4 text-[#0D99FF]" />
                                        )}
                                        <h3 className="text-lg font-semibold text-[#0D99FF]">
                                          Question {index + 1}
                                        </h3>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-lg font-bold text-[#0D99FF]">
                                          {question.marks.toFixed(1)}/
                                          {question.totalMarks.toFixed(1)}
                                        </span>
                                        <p className="text-xs text-[#0D99FF]/70">
                                          Score
                                        </p>
                                      </div>
                                    </div>
                                    {step3ExpandedQuestions.has(
                                      question.id
                                    ) && (
                                      <div className="p-4">
                                        <div className="flex gap-3 mb-3">
                                          {/* Question Image - Only show if questionImage exists */}
                                          {question.questionImage && (
                                            <div className="w-1/3 flex-shrink-0">
                                              <img
                                                src={
                                                  typeof question.questionImage ===
                                                  "string"
                                                    ? question.questionImage
                                                    : URL.createObjectURL(
                                                        question.questionImage
                                                      )
                                                }
                                                alt={`Question ${index + 1}`}
                                                className="w-full h-40 object-contain border rounded-lg bg-gray-50 cursor-zoom-in"
                                                onClick={() => {
                                                  const questionImageSrc =
                                                    typeof question.questionImage ===
                                                    "string"
                                                      ? question.questionImage
                                                      : URL.createObjectURL(
                                                          question.questionImage
                                                        );
                                                  handleImagePreview(
                                                    questionImageSrc,
                                                    `Question ${index + 1}`,
                                                    [questionImageSrc],
                                                    0
                                                  );
                                                }}
                                              />
                                            </div>
                                          )}
                                          <div
                                            className={
                                              question.questionImage
                                                ? "flex-1"
                                                : "w-full"
                                            }
                                          >
                                            <p className="text-gray-700 font-medium text-sm mb-2">
                                              Question Content:
                                            </p>
                                            <div className="bg-gray-100 border border-gray-200 p-3 rounded-lg">
                                              <p className="text-sm text-gray-800 mb-2">
                                                <strong>Problem:</strong>{" "}
                                                <MathJaxRenderer className="inline">
                                                  {applyBasicMarkdown(
                                                    question.question_text ||
                                                      question.extractedContent
                                                        ?.problem ||
                                                      "Question content not available."
                                                  )}
                                                </MathJaxRenderer>
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="mb-3">
                                          {/* Answer Images - Only show if answerImages exist and are not empty */}
                                          {question.answerImages &&
                                            question.answerImages.length >
                                              0 && (
                                              <>
                                                <p className="text-gray-700 font-medium text-sm mb-1">
                                                  Your Answer Images:
                                                </p>
                                                <div className="flex overflow-x-auto gap-2 p-2 border border-gray-200 rounded-lg bg-white">
                                                  {question.answerImages.map(
                                                    (img, imgIndex) => {
                                                      const imageSrc =
                                                        typeof img === "string"
                                                          ? img
                                                          : URL.createObjectURL(
                                                              img
                                                            );
                                                      return (
                                                        <img
                                                          key={imgIndex}
                                                          src={imageSrc}
                                                          alt={`Answer ${
                                                            imgIndex + 1
                                                          }`}
                                                          className="h-28 w-auto object-contain border rounded-lg flex-shrink-0 cursor-zoom-in"
                                                          onClick={() =>
                                                            handleImagePreview(
                                                              imageSrc,
                                                              `Answer ${
                                                                imgIndex + 1
                                                              } - Question ${
                                                                index + 1
                                                              }`,
                                                              question.answerImages.map(
                                                                (img) =>
                                                                  typeof img ===
                                                                  "string"
                                                                    ? img
                                                                    : URL.createObjectURL(
                                                                        img
                                                                      )
                                                              ),
                                                              imgIndex
                                                            )
                                                          }
                                                        />
                                                      );
                                                    }
                                                  )}
                                                </div>
                                              </>
                                            )}

                                          {/* Answer Text - Show if answer_text exists or if no answer images */}
                                          {(question.answer_text ||
                                            !question.answerImages ||
                                            question.answerImages.length ===
                                              0) && (
                                            <>
                                              <p className="text-gray-700 font-medium text-sm mb-1">
                                                Your Answer:
                                              </p>
                                              <div className="bg-gray-100 border border-gray-200 p-3 rounded-lg">
                                                <div className="text-sm text-gray-800">
                                                  <MathJaxRenderer className="whitespace-pre-line">
                                                    {applyBasicMarkdown(
                                                      question.answer_text ||
                                                        "Answer not available."
                                                    )}
                                                  </MathJaxRenderer>
                                                </div>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                        {/* Feedback & Analysis with Step-by-Step table */}
                                        <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
                                          <div className="flex items-center justify-between mb-3">
                                            <p className="text-gray-800 font-medium text-sm">
                                              Feedback & Analysis:
                                            </p>
                                          </div>
                                          <p className="text-[#0D99FF] font-semibold text-base">
                                            Question score : {question.marks}/
                                            {question.totalMarks.toFixed(1)}
                                          </p>
                                          <div className="text-sm text-gray-700 mb-4 leading-relaxed">
                                            <MathJaxRenderer className="leading-relaxed">
                                              {applyBasicMarkdown(
                                                question.feedback ||
                                                  "This is a preview of qualitative feedback for your answer. In the final evaluation, AI will provide detailed comments and suggestions to improve your solution."
                                              )}
                                            </MathJaxRenderer>
                                          </div>
                                          {/* Step-by-Step Evaluation Table - Only render if stepBreakdown exists and is not empty */}
                                          {question.stepBreakdown &&
                                            question.stepBreakdown.length >
                                              0 && (
                                              <div className="mb-2">
                                                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                                  Step-by-Step Evaluation:
                                                </h4>
                                                <div className="overflow-x-auto">
                                                  <table className="w-full text-sm">
                                                    <thead>
                                                      <tr className="bg-gray-50 border-b border-gray-200">
                                                        <th className="text-left py-2 px-3 font-medium text-gray-700 w-20 min-w-[80px]">
                                                          Step
                                                        </th>
                                                        <th className="text-left py-2 px-3 font-medium text-gray-700">
                                                          Feedback
                                                        </th>
                                                        <th className="text-right py-2 px-3 font-medium text-gray-700">
                                                          Marks
                                                        </th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {question.stepBreakdown.map(
                                                        (row, i) => (
                                                          <tr
                                                            key={i}
                                                            className="border-b border-gray-100"
                                                          >
                                                            <td className="py-2 px-3 text-gray-600 font-medium w-20 min-w-[80px] whitespace-nowrap">
                                                              Step{" "}
                                                              {row.step ||
                                                                i + 1}
                                                            </td>
                                                            <td className="py-2 px-3 text-gray-700 align-top">
                                                              <FeedbackContent
                                                                row={row}
                                                                onAskDoubt={() => {
                                                                  const questionIdentifier = `${
                                                                    step3Data?.id ??
                                                                    currentRequestId ??
                                                                    "step3-preview"
                                                                  }-${
                                                                    question?.id ??
                                                                    question?.questionId ??
                                                                    question?.question_id ??
                                                                    question?.questionNumber ??
                                                                    question?.question_number ??
                                                                    index
                                                                  }`;
                                                                  const doubtText =
                                                                    "Why did lose marks for the step " +
                                                                    row.step +
                                                                    "? " +
                                                                    row.feedback;
                                                                  handleAskDoubtPrefill(
                                                                    questionIdentifier,
                                                                    doubtText
                                                                  );
                                                                }}
                                                              />
                                                            </td>{" "}
                                                            <td className="py-2 px-3 text-right text-gray-800 font-semibold">
                                                              {(
                                                                row.marks || 0
                                                              ).toFixed(1)}
                                                              /
                                                              {(
                                                                row.maxMarks ||
                                                                0
                                                              ).toFixed(1)}
                                                            </td>
                                                          </tr>
                                                        )
                                                      )}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            {shouldShowRightUtilityPanel && (
                              <>
                                <div
                                  className="w-4 self-stretch flex flex-col justify-center items-center cursor-col-resize"
                                  onMouseDown={handleMouseDown}
                                  title="Drag to resize"
                                >
                                  <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                                  <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                                    <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                                    </div>
                                  </div>
                                  <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                                </div>
                                <div
                                  className="flex flex-col h-full border-l border-gray-200 relative"
                                  style={{
                                    width: `${100 - leftPanelWidth}%`,
                                  }}
                                >
                                  <button
                                    onClick={() => setShowSidePanel(false)}
                                    className="absolute top-3 right-3 text-[#0D99FF] hover:text-[#5d4de2] transition-colors p-2 rounded-full bg-white shadow z-99"
                                    aria-label="Close doubt solver panel"
                                  >
                                    <FaTimes className="w-8 h-8" />
                                  </button>
                                  <div className="flex-1 overflow-hidden">
                                    <DoubtSolver
                                      disableWorkbookMode={true}
                                      prefilledTextProp={askDoubtText}
                                      autoSendPrefilledText={false}
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center min-h-[400px]">
                            <p className="text-gray-500">
                              No evaluation data available
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                </div>
              </div>

              {/* Footer */}
              <div
                className="flex items-center justify-end gap-3 px-6 py-2 border-t bg-gray-50 flex-shrink-0"
                style={{ height: "10%", minHeight: "60px" }}
              >
                {currentStep !== 3 && (
                  <button
                    onClick={() => {
                      if (currentStep === 1) {
                        setShowUploadModal(false);
                        setShowSidePanel(false);
                        setAskDoubtText("");
                        setShowSidePanel(false);
                        setAskDoubtText("");
                      } else {
                        setCurrentStep((prev) => Math.max(1, prev - 1));
                      }
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {currentStep === 1 ? "Cancel" : "Back"}
                  </button>
                )}
                {/* Multistep controls: Next only, no back to done steps (applies to both modes) */}
                <>
                  {currentStep === 1 && (
                    <button
                      onClick={() => {
                        if (isSuperAdmin) {
                          goToStep2();
                        } else {
                          uploadStudentAnswers();
                        }
                      }}
                      disabled={
                        step1QuestionImages.length === 0 ||
                        step1AnswerImages.length === 0 ||
                        isUploading
                      }
                      className={`px-6 py-2 rounded-lg text-white transition-colors ${
                        step1QuestionImages.length === 0 ||
                        step1AnswerImages.length === 0 ||
                        isUploading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-[#0D99FF] hover:bg-[#6B56E8]"
                      }`}
                    >
                      {isUploading ? "Uploading..." : "Upload & Process"}
                    </button>
                  )}
                  {!STEP2_DISABLED && currentStep === 2 && (
                    <button
                      onClick={handleProcessForEvaluation}
                      className="px-6 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#6B56E8] transition-colors"
                    >
                      Process for Evaluation
                    </button>
                  )}
                  {currentStep === 3 && (
                    <button
                      onClick={() => {
                        // Reset all state variables and loaders
                        setShowUploadModal(false);
                        setShowSidePanel(false);
                        setAskDoubtText("");
                        setCurrentStep(1);
                        setCompletedSteps(new Set());
                        setStep1QuestionImages([]);
                        setStep1AnswerImages([]);
                        setStep1QuestionThumbnails(new Map());
                        setIsUploading(false);
                        setShowConfirmationModal(false);
                        setStep1ApiResponse(null);
                        setIsStep3Loading(false);
                        setIsStep2Loading(false);
                        setVerifiedQuestions(new Set());
                        setPreviewSheet(null);
                        setStep2ExpandedQuestions(new Set([1]));
                        setStep3ExpandedQuestions(new Set([1]));
                        setStep2Questions([]);
                        setStep3Data(null);
                        setShowImagePreview(false);
                        setPreviewImage(null);
                        setPreviewImageAlt("");
                        setPreviewImages([]);
                        setPreviewIndex(0);
                        setExpandedQuestions(new Set([1]));
                        setUploadData({
                          questions: [
                            {
                              id: 1,
                              questionImage: null,
                              answerImages: [],
                              marks: 0,
                            },
                          ],
                        });
                        step2SeededRef.current = false;

                        // Reset SSE state
                        setIsSSEProcessing(false);
                        setSseProgress(0);
                        setSseStatus("idle");
                        setSseError(null);
                        setCurrentRequestId(null);
                        if (sseSubscriptionRef.current) {
                          try {
                            sseSubscriptionRef.current();
                          } catch {
                            // ignore
                          }
                          sseSubscriptionRef.current = null;
                        }
                      }}
                      className="px-6 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#6B56E8] transition-colors"
                    >
                      Done
                    </button>
                  )}
                </>
              </div>
            </div>
          </div>
        )}

        {/* Results Modal - Full Screen */}
        {showResultsModal && selectedSheet && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-full h-full flex flex-col relative overflow-hidden">
              <div
                className="flex flex-1 min-h-0 overflow-hidden relative"
                ref={resizeRef}
              >
                <div className="absolute top-4 right-4 flex items-center gap-2 z-50">
                  <button
                    onClick={() => {
                      if (showSidePanel) {
                        setShowSidePanel(false);
                      } else {
                        fetchEvaluatedSheets();
                        setShowResultsModal(false);
                      }
                    }}
                    className="text-[#0D99FF] hover:text-[#5d4de2] transition-colors p-2 rounded-full bg-white/80 backdrop-blur-sm shadow hover:bg-[#0D99FF]/10"
                    aria-label="Close results"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
                {/* shouldShowLeftUtilityPanel */}
                {shouldShowLeftUtilityPanel && (
                  <>
                    {renderLeftUtilityPanel(leftInfoPanelWidth)}
                    <div
                      className="w-4 h-full flex flex-col justify-center items-center cursor-col-resize"
                      onMouseDown={handleLeftResizerMouseDown}
                      title="Drag to resize"
                    >
                      <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                      <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                        <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                        </div>
                      </div>
                      <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                    </div>
                  </>
                )}
                {/* Center Panel - GoalQuestions main content */}
                <div
                  className={`flex flex-col bg-gray-50 z-40 relative min-h-0 overflow-hidden ${
                    isMobile
                      ? ""
                      : showSidePanel
                      ? "border-r border-gray-200"
                      : ""
                  }`}
                  style={{
                    width: centerPanelWidth,
                  }}
                >
                  <div
                    className="flex-1 overflow-y-auto p-6 pb-32 bg-gray-50"
                    style={{ minHeight: 0 }}
                  >
                    <div className="space-y-4 w-[90%] mx-auto">
                      <div className="flex items-center justify-between px-4 py-1 border-2 border-[#0D99FF]/20 rounded-lg bg-gradient-to-r from-[#0D99FF]/5 to-[#0D99FF]/10 shadow-sm">
                        <div className="flex-1">
                          <h2 className="text-lg font-bold text-gray-800 mb-0.5">
                            {selectedSheet.fileName || "Worksheet Evaluation"}
                          </h2>
                          <p className="text-xs text-gray-600">
                            {selectedSheet.subject
                              ? `${selectedSheet.subject} Evaluation`
                              : "Evaluation Summary"}
                          </p>
                        </div>
                        <div className="text-center">
                          <p
                            style={{ marginBottom: "0px" }}
                            className="text-xs font-medium text-gray-600 mb-0"
                          >
                            Overall Score
                          </p>
                          <div className="flex items-baseline justify-center gap-0.5">
                            <span className="text-lg font-bold text-[#0D99FF]">
                              {Number.isFinite(selectedSheetOverallMarks)
                                ? selectedSheetOverallMarks
                                : 0}
                            </span>
                            <span className="text-sm text-gray-500">/</span>
                            <span className="text-base font-semibold text-gray-700">
                              {Number.isFinite(selectedSheetTotalMarks)
                                ? selectedSheetTotalMarks
                                : 0}
                            </span>
                          </div>
                          <div className="rounded-full text-center">
                            <span className="text-sm font-semibold text-[#0D99FF]">
                              {` ( ${selectedSheetPercentage}% )`}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {selectedSheetQuestions.length > 0 ? (
                          selectedSheetQuestions.map((question, index) => {
                            const questionId = question?.id ?? index + 1;
                            const isExpanded =
                              expandedQuestions.has(questionId);
                            const marksAwarded = Number(question?.marks ?? 0);
                            const totalMarks = Number(
                              question?.totalMarks ?? 0
                            );
                            const formattedMarks = Number.isFinite(marksAwarded)
                              ? marksAwarded.toFixed(1)
                              : (0).toFixed(1);
                            const formattedTotal =
                              Number.isFinite(totalMarks) && totalMarks > 0
                                ? Number.isInteger(totalMarks)
                                  ? totalMarks.toString()
                                  : totalMarks.toFixed(1)
                                : "0";
                            const normalizedAnswerImages = Array.isArray(
                              question?.answerImages
                            )
                              ? question.answerImages
                              : [];
                            const questionImageSrc =
                              typeof question?.questionImage === "string"
                                ? question.questionImage
                                : question?.questionImage
                                ? URL.createObjectURL(question.questionImage)
                                : null;

                            return (
                              <div
                                key={questionId}
                                className="border-2 border-[#0D99FF]/20 rounded-lg shadow-sm bg-white"
                              >
                                <div
                                  className="flex items-center justify-between p-3 bg-white border-b border-[#0D99FF]/20 rounded-t-lg cursor-pointer hover:bg-[#0D99FF]/5 transition-colors"
                                  onClick={() => toggleQuestion(questionId)}
                                >
                                  <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                      <FaChevronDown className="w-4 h-4 text-[#0D99FF]" />
                                    ) : (
                                      <FaChevronRight className="w-4 h-4 text-[#0D99FF]" />
                                    )}
                                    <h3 className="text-lg font-semibold text-[#0D99FF]">
                                      Question {index + 1}
                                    </h3>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-lg font-bold text-[#0D99FF]">
                                      {formattedMarks}/{formattedTotal}
                                    </span>
                                    <p className="text-xs text-[#0D99FF]/70">
                                      Score
                                    </p>
                                  </div>
                                </div>

                                {isExpanded && (
                                  <div className="p-4">
                                    <div className="flex flex-col md:flex-row gap-3 mb-3">
                                      {questionImageSrc && (
                                        <div className="md:w-1/3 w-full flex-shrink-0">
                                          <img
                                            src={questionImageSrc}
                                            alt={`Question ${index + 1}`}
                                            className="w-full h-40 object-contain border rounded-lg bg-gray-50 cursor-zoom-in"
                                            onClick={() =>
                                              handleImagePreview(
                                                questionImageSrc,
                                                `Question ${index + 1}`,
                                                [questionImageSrc],
                                                0
                                              )
                                            }
                                          />
                                        </div>
                                      )}
                                      <div
                                        className={
                                          questionImageSrc ? "flex-1" : "w-full"
                                        }
                                      >
                                        <p className="text-gray-700 font-medium text-sm mb-2">
                                          Question Content:
                                        </p>
                                        <div className="bg-gray-100 border border-gray-200 p-3 rounded-lg">
                                          <p className="text-sm text-gray-800 mb-2">
                                            <strong>Problem:</strong>{" "}
                                            <MathJaxRenderer className="inline">
                                              {applyBasicMarkdown(
                                                question?.question_text ||
                                                  question?.extractedContent
                                                    ?.problem ||
                                                  "Question content not available."
                                              )}
                                            </MathJaxRenderer>
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="mb-3 space-y-3">
                                      {normalizedAnswerImages.length > 0 && (
                                        <div>
                                          <p className="text-gray-700 font-medium text-sm mb-1">
                                            Your Answer Images:
                                          </p>
                                          <div className="flex overflow-x-auto gap-2 p-2 border border-gray-200 rounded-lg bg-white">
                                            {normalizedAnswerImages.map(
                                              (img, imgIndex) => {
                                                const imageSrc =
                                                  typeof img === "string"
                                                    ? img
                                                    : URL.createObjectURL(img);
                                                return (
                                                  <img
                                                    key={imgIndex}
                                                    src={imageSrc}
                                                    alt={`Answer ${
                                                      imgIndex + 1
                                                    }`}
                                                    className="h-28 w-auto object-contain border rounded-lg flex-shrink-0 cursor-zoom-in hover:shadow-lg transition-shadow"
                                                    onClick={() =>
                                                      handleImagePreview(
                                                        imageSrc,
                                                        `Answer ${
                                                          imgIndex + 1
                                                        } - Question ${
                                                          index + 1
                                                        }`,
                                                        normalizedAnswerImages.map(
                                                          (item) =>
                                                            typeof item ===
                                                            "string"
                                                              ? item
                                                              : URL.createObjectURL(
                                                                  item
                                                                )
                                                        ),
                                                        imgIndex
                                                      )
                                                    }
                                                    onError={(e) => {
                                                      e.target.src =
                                                        "/images/avatar1.png";
                                                    }}
                                                  />
                                                );
                                              }
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {(question?.answer_text ||
                                        normalizedAnswerImages.length ===
                                          0) && (
                                        <div>
                                          <p className="text-gray-700 font-medium text-sm mb-1">
                                            Your Answer:
                                          </p>
                                          <div className="bg-gray-100 border border-gray-200 p-3 rounded-lg">
                                            <div className="text-sm text-gray-800">
                                              <MathJaxRenderer className="whitespace-pre-line">
                                                {applyBasicMarkdown(
                                                  question?.answer_text ||
                                                    "Answer not available."
                                                )}
                                              </MathJaxRenderer>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
                                      <div className="flex items-center justify-between mb-3">
                                        <p className="text-gray-800 font-medium text-sm">
                                          Feedback & Analysis:
                                        </p>
                                      </div>
                                      <p className="text-[#0D99FF] font-semibold text-base">
                                        Question score : {formattedMarks}/
                                        {formattedTotal}
                                      </p>
                                      <div className="text-sm text-gray-700 mb-4 leading-relaxed">
                                        <MathJaxRenderer className="leading-relaxed">
                                          {applyBasicMarkdown(
                                            question?.feedback ||
                                              "Detailed feedback will appear here once evaluation is complete."
                                          )}
                                        </MathJaxRenderer>
                                      </div>

                                      {Array.isArray(question?.stepBreakdown) &&
                                        question.stepBreakdown.length > 0 && (
                                          <div className="mb-2">
                                            <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                              Step-by-Step Evaluation:
                                            </h4>
                                            <div className="overflow-x-auto">
                                              <table className="w-full text-sm">
                                                <thead>
                                                  <tr className="bg-gray-50 border-b border-gray-200">
                                                    <th className="text-left py-2 px-3 font-medium text-gray-700 w-20 min-w-[80px]">
                                                      Step
                                                    </th>
                                                    <th className="text-left py-2 px-3 font-medium text-gray-700">
                                                      Feedback
                                                    </th>
                                                    <th className="text-right py-2 px-3 font-medium text-gray-700">
                                                      Marks
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {question.stepBreakdown.map(
                                                    (row, rowIndex) => (
                                                      <tr
                                                        key={rowIndex}
                                                        className="border-b border-gray-100"
                                                      >
                                                        <td className="py-2 px-3 text-gray-600 font-medium w-20 min-w-[80px] whitespace-nowrap">
                                                          Step{" "}
                                                          {row?.step ??
                                                            rowIndex + 1}
                                                        </td>
                                                        <td className="py-2 px-3 text-gray-700 align-top">
                                                          <FeedbackContent
                                                            row={row}
                                                            onAskDoubt={() => {
                                                              const problemText =
                                                                question?.question_text ||
                                                                "";
                                                              const solutionText =
                                                                question?.answer_text ||
                                                                "";
                                                              const stepFeedback =
                                                                row?.feedback ||
                                                                "";
                                                              const doubtText = `For the problem - ${problemText}\nand for my solution - ${solutionText}\n\nwhy is the step below not correct -\n${stepFeedback}`;
                                                              const sheetIdentifier =
                                                                selectedSheet?.id ??
                                                                selectedSheet?.worksheetId ??
                                                                selectedSheet?.worksheet_id ??
                                                                selectedSheet?.requestId ??
                                                                selectedSheet?.request_id ??
                                                                "selected-sheet";
                                                              const questionIdentifier = `${sheetIdentifier}-${questionId}`;
                                                              handleAskDoubtPrefill(
                                                                questionIdentifier,
                                                                doubtText
                                                              );
                                                            }}
                                                          />
                                                        </td>
                                                        <td className="py-2 px-3 text-right text-gray-800 font-semibold align-top">
                                                          {Number.isFinite(
                                                            Number(row?.marks)
                                                          )
                                                            ? Number(
                                                                row?.marks
                                                              ).toFixed(1)
                                                            : "0.0"}
                                                          /
                                                          {Number.isFinite(
                                                            Number(
                                                              row?.maxMarks
                                                            )
                                                          )
                                                            ? Number(
                                                                row?.maxMarks
                                                              ).toFixed(1)
                                                            : "0.0"}
                                                        </td>
                                                      </tr>
                                                    )
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="py-12 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg bg-white">
                            No questions available for this evaluation yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="fixed bottom-6 right-6 z-50">
                    <button
                      type="button"
                      onClick={handleShareResults}
                      className="flex items-center gap-2 px-6 py-3 bg-[#0D99FF] text-white rounded-full shadow-xl hover:bg-[#5d4de2] focus:outline-none focus:ring-2 focus:ring-[#0D99FF]/50 transition-colors"
                    >
                      <FaShareAlt className="w-4 h-4" />
                      <span className="text-sm font-semibold tracking-wide">
                        Share with friends
                      </span>
                    </button>
                  </div>
                </div>

                {/* Resizer + Right Panel (desktop only, visible after Need Help) */}
                {shouldShowRightUtilityPanel && (
                  <>
                    <div
                      className="w-4 h-full flex flex-col justify-center items-center cursor-col-resize"
                      onMouseDown={handleMouseDown}
                      title="Drag to resize"
                    >
                      <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                      <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                        <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                          <div className="w-1 h-1 bg-gray-700 rounded-full" />
                        </div>
                      </div>
                      <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                    </div>

                    {/* Right Panel - Doubt solver */}
                    <div
                      className="flex flex-col"
                      style={{
                        width: `${100 - leftPanelWidth}%`,
                      }}
                    >
                      <DoubtSolver
                        disableWorkbookMode={true}
                        prefilledTextProp={askDoubtText}
                        autoSendPrefilledText={false}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {showImagePreview && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl relative">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b bg-[#0D99FF] text-white rounded-t-lg">
                <h3 className="text-lg font-semibold">{previewImageAlt}</h3>
                <button
                  onClick={() => setShowImagePreview(false)}
                  className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              {/* Image/PDF Content */}
              <div className="flex-1 overflow-auto p-4 bg-gray-50 relative">
                {previewImages.length > 1 && (
                  <button
                    aria-label="Previous image"
                    onClick={goPrevPreview}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-[#0D99FF] rounded-full p-2 shadow"
                  >
                    <FaChevronLeft className="w-6 h-6" />
                  </button>
                )}
                {(() => {
                  const current = previewImages[previewIndex] || {
                    src: previewImage,
                    isPdf: false,
                  };
                  if (current.isPdf) {
                    return (
                      <iframe
                        src={current.src}
                        title={previewImageAlt}
                        className="w-full h-[70vh] rounded-lg border shadow-lg bg-white"
                      />
                    );
                  }
                  return (
                    <img
                      src={current.src}
                      alt={previewImageAlt}
                      className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                      onError={(e) => {
                        e.target.src = "/images/avatar1.png";
                      }}
                    />
                  );
                })()}
                {previewImages.length > 1 && (
                  <button
                    aria-label="Next image"
                    onClick={goNextPreview}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-[#0D99FF] rounded-full p-2 shadow"
                  >
                    <FaChevronRight className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Footer intentionally omitted */}
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmationModal && (
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 9999,
            }}
          >
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-2xl border">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  You want to go to evaluation step without verifying questions
                  & answers matching?
                </h3>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleConfirmationYes}
                    className="px-6 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#6B56E8] transition-colors"
                  >
                    Yes
                  </button>
                  <button
                    onClick={handleConfirmationNo}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <RateLimitModal
          isOpen={isRateLimitModalOpen}
          serverMessage={rateLimitServerMessage}
          supportMessage="You've reached the current evaluation limit. Please wait a few minutes before trying again or contact support to raise your limit."
          onClose={handleRateLimitModalClose}
        />
      </div>
    </MathJaxContext>
  );
};

export default EvaluateAnswerSheet;
